// UI.ts: Implements dynamic, adaptive visualization and controls for neural network simulation.
// Purpose: Realize the vision of adaptive, extensible UI for living neural network simulation, fractal/wave visualization, and psychic potential comparison.

// Fix: ensure nodeType matches the type from './types'
import { NeuralNetworkSimulator } from './NeuralNetworkSimulator';
import { NodeType, Node, Edge } from './types';
import { comparePsychicPotential } from './PsychicPotential';
import { createPhysicalSoundSynthesizer, PhysicalSoundSynthesizer } from './physicalSoundSynthesis';
import { ModernUI } from './ui/ModernUI';
import { VisualEvolutionEngine, VisualProperties, EvolutionStage } from './visualEvolution';
import { getPerformanceTrail } from './core/performanceMonitor';
import { getQualityTierManager, getRenderScaler, getUpdateThrottler, computeSceneElementState, shouldRenderEdge, shouldRenderGlow, shouldEmitParticle, getAudioEngine } from './core';
import type { QualityTierManager, RenderScaler, UpdateThrottler, LodQualityState, SceneElementState } from './core';

export class UI {
	network: NeuralNetworkSimulator;
	canvas: HTMLCanvasElement;
	ctx: CanvasRenderingContext2D | null;
	infoDiv: HTMLDivElement;
	titleDiv: HTMLDivElement;
	statsDiv: HTMLDivElement;
	restartBtn: HTMLButtonElement;
	playPauseBtn: HTMLButtonElement;
	mouse: { x: number; y: number } | null = null;
	// Audio context is managed by the network's AudioEngine
	private heartHumOsc: OscillatorNode | null = null;
	private heartHumGain: GainNode | null = null;
	
	// Voice control methods
	cyclePersonality: () => void;
	cycleEmotion: () => void;
	private lastHeartPulseStep: number = -1;
	private reverbNode: ConvolverNode | null = null;
	
	// Creature voice integration (intrinsic to the creature) - now in sidebar

	// Sound statistics tracking
	private buzzCountTotal: number = 0;
	private sparkleCountTotal: number = 0;
	private heartHumStartCount: number = 0;
	// (UI no longer triggers lightning zaps directly; simulator handles audio)

	// 3D rotation angles
	private rotation: { x: number, y: number };

	// Add zoom/magnify state
	private zoomCenter: { x: number, y: number } | null = null;
	private zoomRadius: number = 360;
	private zoomScale: number = 100.2;
	_sunHazeOsc: OscillatorNode | null = null;
	_sunHazeGain: GainNode | null = null;
	
	// Physical sound synthesis
	private physicalSynthesizer: PhysicalSoundSynthesizer | null = null;
	
	// Modern UI integration
	private modernUI: ModernUI | null = null;
	private useModernUI: boolean = true; // Toggle to enable/disable modern UI
	
	// Equilibrium layout state
	private equilibriumCanvas: HTMLCanvasElement | null = null;
	private equilibriumCtx: CanvasRenderingContext2D | null = null;
	private equilibriumWidth: number = 0;
	private equilibriumHeight: number = 0;

	// Performance optimization: cache canvas size to avoid expensive resize every frame
	private lastCanvasWidth: number = 0;
	private lastCanvasHeight: number = 0;
	private lastNodeCount: number = -1;

	// Visual evolution engine
	private visualEvolution: VisualEvolutionEngine;
	private lastFrameTime: number = 0;

	// ── LOD / Performance Scaling System ──
	private qualityManager: QualityTierManager;
	private renderScaler: RenderScaler;
	private updateThrottler: UpdateThrottler;
	private lastLodState: LodQualityState | null = null;
	private frameCount: number = 0;

	constructor(network: NeuralNetworkSimulator) {
		this.visualEvolution = new VisualEvolutionEngine();
		this.lastFrameTime = Date.now();
		
		// ── Initialize LOD / Performance Scaling ──
		this.qualityManager = getQualityTierManager('low'); // Efficiency-first: start at 'low'
		this.renderScaler = getRenderScaler();
		this.updateThrottler = getUpdateThrottler();
		this.visualEvolution.enableExternalLOD(); // Use external quality manager for LOD
		this.network = network;
		this.canvas = document.createElement('canvas');
		// Canvas fills window, stays in background
		this.canvas.style.position = 'fixed';
		this.canvas.style.left = '0';
		this.canvas.style.top = '0';
		this.canvas.style.width = '100vw';
		this.canvas.style.height = '100vh';
		this.canvas.style.zIndex = '-1';
		this.canvas.style.margin = '0';
		this.canvas.style.pointerEvents = 'none';
		document.body.appendChild(this.canvas);

		this.ctx = this.canvas.getContext('2d');

		// Create a top bar for title/info
		this.titleDiv = document.createElement('div');
		this.titleDiv.style.position = 'fixed';
		this.titleDiv.style.top = '0';
		this.titleDiv.style.left = '0';
		this.titleDiv.style.width = '100vw';
		this.titleDiv.style.height = '56px';
		this.titleDiv.style.background = 'rgba(56, 56, 56, 0.53)';
		this.titleDiv.style.textAlign = 'center';
		this.titleDiv.style.fontSize = '2em';
		this.titleDiv.style.fontWeight = 'bold';
		this.titleDiv.style.lineHeight = '56px';
		this.titleDiv.style.zIndex = '10';
		this.titleDiv.style.boxShadow = '0 2px 8px #45027822';
		this.titleDiv.style.color = '#fff'; // <-- ensure white text
		document.body.appendChild(this.titleDiv);

	this.infoDiv = document.createElement('div');
	this.infoDiv.style.display = 'none';
	document.body.appendChild(this.infoDiv);

	this.statsDiv = document.createElement('div');
	this.statsDiv.style.position = 'fixed';
	this.statsDiv.style.top = '72px';
	this.statsDiv.style.right = '16px';
	this.statsDiv.style.bottom = '176px';
	this.statsDiv.style.width = 'min(360px, calc(100vw - 24px))';
	this.statsDiv.style.height = 'auto';
	this.statsDiv.style.maxHeight = 'calc(100vh - 248px)';
		this.statsDiv.style.background = 'rgba(25, 25, 30, 0.86)';
		this.statsDiv.style.border = '1px solid rgba(160, 60, 255, 0.25)';
		this.statsDiv.style.borderRadius = '12px';
		this.statsDiv.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.45)';
		this.statsDiv.style.padding = '16px';
		this.statsDiv.style.overflowY = 'auto';
		this.statsDiv.style.fontSize = '0.95em';
		this.statsDiv.style.lineHeight = '1.35';
		this.statsDiv.style.zIndex = '10000';
		this.statsDiv.style.color = '#fff'; // <-- ensure white text
		document.body.appendChild(this.statsDiv);

		// ── Dedicated psychic stats element (avoids innerHTML += on massive DOM) ──
		const psychicDiv = document.createElement('div');
		psychicDiv.style.position = 'fixed';
		psychicDiv.style.bottom = '24px';
		psychicDiv.style.right = '16px';
		psychicDiv.style.width = 'min(360px, calc(100vw - 24px))';
		psychicDiv.style.maxHeight = '120px';
		psychicDiv.style.background = 'rgba(25, 25, 30, 0.86)';
		psychicDiv.style.border = '1px solid rgba(160, 60, 255, 0.25)';
		psychicDiv.style.borderRadius = '12px';
		psychicDiv.style.padding = '10px 16px';
		psychicDiv.style.overflowY = 'auto';
		psychicDiv.style.fontSize = '0.85em';
		psychicDiv.style.lineHeight = '1.3';
		psychicDiv.style.zIndex = '10000';
		psychicDiv.style.color = '#fff';
		psychicDiv.style.display = 'none';
		psychicDiv.id = 'neuro-psychic-panel';
		document.body.appendChild(psychicDiv);
		(this as any)._psychicDiv = psychicDiv;

		// Restart button on top left corner
		this.restartBtn = document.createElement('button');
		this.restartBtn.textContent = 'Restart Simulation';
		this.restartBtn.style.position = 'fixed';
		this.restartBtn.style.left = '32px';
		this.restartBtn.style.top = '24px';
		this.restartBtn.style.padding = '10px 24px';
		this.restartBtn.style.fontSize = '1.1em';
		this.restartBtn.style.background = '#450278';
		this.restartBtn.style.color = '#fff';
		this.restartBtn.style.border = 'none';
		this.restartBtn.style.borderRadius = '8px';
		this.restartBtn.style.boxShadow = '0 2px 8px #45027822, 0 0 24px #a03cff44';
		this.restartBtn.style.cursor = 'pointer';
		this.restartBtn.style.transition = 'box-shadow 0.3s';
		this.restartBtn.onmouseenter = () => {
			this.restartBtn.style.boxShadow = '0 2px 16px #45027844, 0 0 48px #e0b0ff88';
		};
		this.restartBtn.onmouseleave = () => {
			this.restartBtn.style.boxShadow = '0 2px 8px #45027822, 0 0 24px #a03cff44';
		};
		this.restartBtn.style.zIndex = '10';
		document.body.appendChild(this.restartBtn);

		// Manual play/pause button (the only pause control)
		this.playPauseBtn = document.createElement('button');
		this.playPauseBtn.textContent = 'Pause Simulation';
		this.playPauseBtn.style.position = 'fixed';
		this.playPauseBtn.style.left = '240px';
		this.playPauseBtn.style.top = '24px';
		this.playPauseBtn.style.padding = '10px 24px';
		this.playPauseBtn.style.fontSize = '1.1em';
		this.playPauseBtn.style.background = '#2f6fdb';
		this.playPauseBtn.style.color = '#fff';
		this.playPauseBtn.style.border = 'none';
		this.playPauseBtn.style.borderRadius = '8px';
		this.playPauseBtn.style.boxShadow = '0 2px 8px rgba(47, 111, 219, 0.35)';
		this.playPauseBtn.style.cursor = 'pointer';
		this.playPauseBtn.style.zIndex = '10';
		document.body.appendChild(this.playPauseBtn);

		// Removed duplicate yellow export button (export available in top header)

		// Load/Save buttons
		const loadBtn = document.createElement('button');
		loadBtn.textContent = 'Load State';
		loadBtn.style.position = 'fixed';
		loadBtn.style.left = '440px';
		loadBtn.style.top = '24px';
		loadBtn.style.padding = '10px 24px';
		loadBtn.style.fontSize = '1.1em';
		loadBtn.style.background = '#45a049';
		loadBtn.style.color = '#fff';
		loadBtn.style.border = 'none';
		loadBtn.style.borderRadius = '8px';
		loadBtn.style.boxShadow = '0 2px 8px #45a04944';
		loadBtn.style.cursor = 'pointer';
		loadBtn.style.zIndex = '10';
		loadBtn.onclick = () => {
			const input = document.createElement('input');
			input.type = 'file';
			input.accept = '.json';
			input.setAttribute('aria-label', 'Load neural network state from file');
			input.onchange = (e: any) => {
				const file = e.target.files[0];
				if (file) {
					const reader = new FileReader();
					reader.onload = (event) => {
						try {
							const content = event.target?.result as string;
							this.network.importState(content);
							this.showNotification('State loaded successfully!');
							console.log('[Import] Simulation state loaded');
						} catch (error) {
							console.error('[Import] Failed to load state:', error);
							this.showNotification('Failed to load state');
						}
					};
					reader.readAsText(file);
				}
			};
			input.click();
		};
		document.body.appendChild(loadBtn);

		// Show equilibrium layout bottom bar by default
		this.showEquilibriumLayout();

		// Animate canvas floating effect
		let floatPhase = 0;
		const animateCanvas = () => {
			floatPhase += 0.02;
			const floatY = Math.sin(floatPhase) * 8;
			this.canvas.style.transform = `translateY(${floatY}px) scale(1.01)`;
			requestAnimationFrame(animateCanvas);
		};
		animateCanvas();

		// Track mouse position for aura (account for CSS transforms like scale/translate)
		this.canvas.addEventListener('mousemove', (e) => {
			const rect = this.canvas.getBoundingClientRect();
			// Map from transformed CSS pixels back to canvas internal coordinates
			const scaleX = rect.width / this.canvas.width;
			const scaleY = rect.height / this.canvas.height;
			this.mouse = {
				x: (e.clientX - rect.left) / (scaleX || 1),
				y: (e.clientY - rect.top) / (scaleY || 1)
			};
		});
		this.canvas.addEventListener('mouseleave', () => {
			this.mouse = null;
		});

		// Add a visible sound enable button if needed
		const soundBtn = document.createElement('button');
		soundBtn.textContent = '🔊 Enable Sound';
		soundBtn.style.position = 'fixed';
		soundBtn.style.bottom = '24px';
		soundBtn.style.left = '24px';
		soundBtn.style.zIndex = '9999';
		soundBtn.style.fontSize = '1.2em';
		soundBtn.style.padding = '10px 24px';
		soundBtn.style.background = '#450278';
		soundBtn.style.color = '#fff';
		soundBtn.style.border = 'none';
		soundBtn.style.borderRadius = '8px';
		soundBtn.style.boxShadow = '0 2px 8px #45027822, 0 0 24px #a03cff44';
		soundBtn.style.cursor = 'pointer';
		soundBtn.style.display = 'none';
		document.body.appendChild(soundBtn);

		const showSoundBtnIfNeeded = () => {
			// Hide sound button since audio is now auto-enabled
			soundBtn.style.display = 'none';
		};

		const createReverb = () => {
			const ctx = this.network.audioContext;
			if (!ctx || this.reverbNode) return;
			const length = ctx.sampleRate * 0.7;
			const impulse = ctx.createBuffer(2, length, ctx.sampleRate);
			for (let c = 0; c < 2; c++) {
				const channel = impulse.getChannelData(c);
				for (let i = 0; i < length; i++) {
					channel[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 2.5);
				}
			}
			this.reverbNode = ctx.createConvolver();
			this.reverbNode.buffer = impulse;
		};

		let audioInitialized = false;
		
		const initAudio = async () => {
			if (audioInitialized) return;
			
			try {
				// Use the network's audio context initialization (which uses AudioEngine)
				this.network.initializeAudioContext();
				
				// Try to resume if suspended
				const ctx = this.network.audioContext;
				if (ctx && ctx.state === 'suspended') {
					await ctx.resume();
				}
				
				// Ensure audio graph is set up and fade in master gain
				this.network.fadeInMasterGain(1.5);
				createReverb();
				
				// Initialize physical sound synthesizer
				this.physicalSynthesizer = createPhysicalSoundSynthesizer(ctx, this.network);
				console.log('[UI] Physical sound synthesizer initialized');
				
				showSoundBtnIfNeeded();
				audioInitialized = true;
				console.log('[Audio] Audio context initialized successfully');
				
				// Start continuous audio synthesis
				setTimeout(() => {
					if (this.network.audioContext && this.network.audioContext.state === 'running') {
						this.network.startContinuousAudioSynthesis();
					}
				}, 500);
			} catch (error) {
				console.warn('[Audio] Failed to initialize audio context:', error);
			}
		};
		
		// Add global error handler for AudioContext errors
		window.addEventListener('error', (event) => {
			if (event.message && event.message.includes('AudioContext')) {
				console.warn('[Audio] Caught AudioContext error:', event.message);
				event.preventDefault();
				return false;
			}
			return true;
		});
		
		// Listen for user interaction to initialize audio (required by browsers)
		const initializeAudioOnce = () => {
			initAudio();
			document.removeEventListener('pointerdown', initializeAudioOnce);
			document.removeEventListener('click', initializeAudioOnce);
			document.removeEventListener('keydown', initializeAudioOnce);
		};
		
		document.addEventListener('pointerdown', initializeAudioOnce);
		document.addEventListener('click', initializeAudioOnce);
		document.addEventListener('keydown', initializeAudioOnce);

		// Best-effort autoplay: attempt to initialize/resume without user input
		const tryAutoInit = () => {
			if (audioInitialized) return;
			initAudio();
		};
		
		// Attempt immediately and retry a few times
		setTimeout(tryAutoInit, 0);
		let autoTries = 0;
		const autoTimer = setInterval(() => {
			if (audioInitialized) { clearInterval(autoTimer); return; }
			if (this.network.audioContext && this.network.audioContext.state === 'running') { clearInterval(autoTimer); return; }
			autoTries++;
			tryAutoInit();
			if (autoTries > 5) clearInterval(autoTimer);
		}, 1500);
		
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible' && !audioInitialized) {
				tryAutoInit();
			}
		});
		window.addEventListener('focus', () => {
			if (!audioInitialized) {
				tryAutoInit();
			}
		});

		soundBtn.onclick = async () => {
			try {
				this.network.initializeAudioContext();
				const ctx = this.network.audioContext;
				if (ctx && ctx.state === 'suspended') {
					await ctx.resume();
				}
				createReverb();
				this.network.fadeInMasterGain(1.5);
				showSoundBtnIfNeeded();
				this.showNotification('Sound enabled!');
			} catch (error) {
				console.warn('[Audio] Failed to enable audio:', error);
				this.showNotification('Failed to enable audio');
			}
		};

		// Periodically check if sound is enabled and show button if not
		setInterval(showSoundBtnIfNeeded, 1000);

		// Connect simulation sound events to UI sound methods
		this.network.onNodeGrowth = async (count: number, color?: string) => {
			const buzzCount = Math.min(count, 5);
			const buzzColor = color || (this.network.nodes.length > 0 ? this.network.nodes[this.network.nodes.length - 1].color : 'hsl(220,70%,60%)');
			for (let i = 0; i < buzzCount; i++) {
				const pan = (i - (buzzCount - 1) / 2) / buzzCount;
				// Evolve buzz sound: modulate by node energy, color, and sunlight
				const energy = this.network.nodes.length > 0 ? this.network.nodes[this.network.nodes.length - 1].energy : 1;
				const sunActive = this.network.sunEnabled ? 1 : 0;
				const freqBase = 220 + energy * 12 + sunActive * 40;
				const duration = 0.22 + Math.min(0.18, energy * 0.01) + sunActive * 0.08;
				const volume = 0.003 + sunActive * 0.0015;
				await this.playBuzz(freqBase, duration, volume, pan, buzzColor);
				this.buzzCountTotal++;
			}
			if (count > 8) {
				// Evolve sparkle: modulate by sunlight and node color
				const sunActive = this.network.sunEnabled ? 1 : 0;
				const sparkleVol = 0.0025 + sunActive * 0.0001;
				await this.playSparkle(sparkleVol, buzzColor);
				this.sparkleCountTotal++;
			}
		};
		this.network.onCrystalHeartPulse = async () => {
			const heartColor = typeof this.network.crystalHeartId === 'number'
			 ? (this.network.nodes[this.network.crystalHeartId]?.color || 'hsl(290,90%,70%)')
			 : 'hsl(290,90%,70%)';
			// Evolve heart hum: modulate by sunlight and heart energy
			const heart = this.network.nodes[this.network.crystalHeartId ?? 0];
			const sunActive = this.network.sunEnabled ? 1 : 0;
			const heartEnergy = heart?.energy ?? 1;
			const freq = 52 + Math.random() * 8 + sunActive * 12 + heartEnergy * 0.01;
			await this.startHeartHum(freq);
			this.heartHumStartCount++;
			if (this.heartHumGain && this.network.audioContext) {
				const ctx = this.network.audioContext;
				const now = ctx.currentTime;
				this.heartHumGain.gain.cancelScheduledValues(now);
				this.heartHumGain.gain.setValueAtTime(this.heartHumGain.gain.value, now);
				this.heartHumGain.gain.linearRampToValueAtTime(0.0001, now + 0.18); // Reduced to prevent clipping
				this.heartHumGain.gain.linearRampToValueAtTime(0.0001, now + 0.6); // Reduced to prevent clipping
			}
			await this.playSparkle(0.0025 + sunActive * 0.0001, heartColor);
			this.sparkleCountTotal++;
		};
		
		
		
	// VOICE PERSONALITY CONTROL BUTTONS
	const voiceControlDiv = document.createElement('div');
	voiceControlDiv.style.position = 'fixed';
	voiceControlDiv.style.bottom = '220px';
	voiceControlDiv.style.left = '24px';
	voiceControlDiv.style.zIndex = '10001'; // Higher z-index to make buttons accessible
	voiceControlDiv.style.display = 'flex';
	voiceControlDiv.style.flexDirection = 'column';
	voiceControlDiv.style.gap = '8px';
	voiceControlDiv.style.pointerEvents = 'auto'; // Ensure buttons are clickable
		
	// Personality buttons
	const personalityBtn = document.createElement('button');
	personalityBtn.textContent = 'Change Personality';
	personalityBtn.style.padding = '6px 12px';
	personalityBtn.style.fontSize = '0.9em';
	personalityBtn.style.background = '#ff6b9d';
	personalityBtn.style.color = '#fff';
	personalityBtn.style.border = 'none';
	personalityBtn.style.borderRadius = '6px';
	personalityBtn.style.cursor = 'pointer';
	personalityBtn.style.pointerEvents = 'auto';
	personalityBtn.style.position = 'relative';
	personalityBtn.style.zIndex = '10001';
	personalityBtn.onclick = () => this.cyclePersonality();
	voiceControlDiv.appendChild(personalityBtn);
		
	// Emotional state button
	const emotionBtn = document.createElement('button');
	emotionBtn.textContent = 'Change Emotion';
	emotionBtn.style.padding = '6px 12px';
	emotionBtn.style.fontSize = '0.9em';
	emotionBtn.style.background = '#4ecdc4';
	emotionBtn.style.color = '#fff';
	emotionBtn.style.border = 'none';
	emotionBtn.style.borderRadius = '6px';
	emotionBtn.style.cursor = 'pointer';
	emotionBtn.style.pointerEvents = 'auto';
	emotionBtn.style.position = 'relative';
	emotionBtn.style.zIndex = '10001';
	emotionBtn.onclick = () => this.cycleEmotion();
	voiceControlDiv.appendChild(emotionBtn);
		
		
		document.body.appendChild(voiceControlDiv);

		// BREATHING SYSTEM CONTROL PANEL
		const breathingControlDiv = document.createElement('div');
		breathingControlDiv.style.position = 'fixed';
		breathingControlDiv.style.bottom = '120px';
		breathingControlDiv.style.right = '24px'; // Move to right side to avoid sidebar
		breathingControlDiv.style.zIndex = '9999';
		breathingControlDiv.style.display = 'flex';
		breathingControlDiv.style.flexDirection = 'column';
		breathingControlDiv.style.gap = '8px';
		breathingControlDiv.style.background = 'rgba(0, 0, 0, 0.8)';
		breathingControlDiv.style.padding = '12px';
		breathingControlDiv.style.borderRadius = '8px';
		breathingControlDiv.style.border = '1px solid #4ecdc4';
		breathingControlDiv.style.maxWidth = '280px'; // Limit width to prevent overlap
		
		// Breathing status display
		const breathingStatusDiv = document.createElement('div');
		breathingStatusDiv.style.color = '#4ecdc4';
		breathingStatusDiv.style.fontSize = '0.9em';
		breathingStatusDiv.style.marginBottom = '8px';
		breathingStatusDiv.innerHTML = '<strong>Breathing System</strong>';
		breathingControlDiv.appendChild(breathingStatusDiv);
		
		// Oxygen level display
		const oxygenDiv = document.createElement('div');
		oxygenDiv.style.color = '#fff';
		oxygenDiv.style.fontSize = '0.8em';
		oxygenDiv.id = 'oxygen-display';
		breathingControlDiv.appendChild(oxygenDiv);
		
		// CO2 level display
		const co2Div = document.createElement('div');
		co2Div.style.color = '#fff';
		co2Div.style.fontSize = '0.8em';
		co2Div.id = 'co2-display';
		breathingControlDiv.appendChild(co2Div);
		
		// Voice capacity display
		const voiceCapacityDiv = document.createElement('div');
		voiceCapacityDiv.style.color = '#fff';
		voiceCapacityDiv.style.fontSize = '0.8em';
		voiceCapacityDiv.id = 'voice-capacity-display';
		breathingControlDiv.appendChild(voiceCapacityDiv);
		
		// Breathing development display
		const developmentDiv = document.createElement('div');
		developmentDiv.style.color = '#4ecdc4';
		developmentDiv.style.fontSize = '0.8em';
		developmentDiv.style.marginTop = '8px';
		developmentDiv.style.padding = '8px';
		developmentDiv.style.background = 'rgba(0, 0, 0, 0.3)';
		developmentDiv.style.borderRadius = '4px';
		developmentDiv.id = 'breathing-development-display';
		breathingControlDiv.appendChild(developmentDiv);
		
		// Breathing control buttons
		const forceBreathBtn = document.createElement('button');
		forceBreathBtn.textContent = 'Force Breath';
		forceBreathBtn.style.padding = '6px 12px';
		forceBreathBtn.style.fontSize = '0.8em';
		forceBreathBtn.style.background = '#4ecdc4';
		forceBreathBtn.style.color = '#fff';
		forceBreathBtn.style.border = 'none';
		forceBreathBtn.style.borderRadius = '4px';
		forceBreathBtn.style.cursor = 'pointer';
		forceBreathBtn.onclick = () => {
			this.network.breathingSystem.forceBreath();
			console.log('[Breathing] Forced breath');
		};
		breathingControlDiv.appendChild(forceBreathBtn);
		
		// Breathing sound toggle
		const soundToggleBtn = document.createElement('button');
		soundToggleBtn.textContent = '🔊 Breathing Sounds';
		soundToggleBtn.style.padding = '6px 12px';
		soundToggleBtn.style.fontSize = '0.8em';
		soundToggleBtn.style.background = '#ff6b9d';
		soundToggleBtn.style.color = '#fff';
		soundToggleBtn.style.border = 'none';
		soundToggleBtn.style.borderRadius = '4px';
		soundToggleBtn.style.cursor = 'pointer';
		soundToggleBtn.style.marginTop = '8px';
		soundToggleBtn.onclick = () => {
			this.toggleBreathingSounds();
		};
		breathingControlDiv.appendChild(soundToggleBtn);
		
		// Respiratory rate adjustment
		const rateSliderDiv = document.createElement('div');
		rateSliderDiv.style.display = 'flex';
		rateSliderDiv.style.alignItems = 'center';
		rateSliderDiv.style.gap = '8px';
		rateSliderDiv.style.marginTop = '8px';
		
		const rateLabel = document.createElement('span');
		rateLabel.textContent = 'Rate:';
		rateLabel.style.color = '#fff';
		rateLabel.style.fontSize = '0.8em';
		rateSliderDiv.appendChild(rateLabel);
		
		const rateSlider = document.createElement('input');
		rateSlider.type = 'range';
		rateSlider.min = '6';
		rateSlider.max = '30';
		rateSlider.value = '12';
		rateSlider.style.width = '100px';
		rateSlider.setAttribute('aria-label', 'Breathing rate control');
		rateSlider.oninput = (e) => {
			const rate = parseInt((e.target as HTMLInputElement).value);
			this.network.breathingSystem.adjustRespiratoryRate(rate);
		};
		rateSliderDiv.appendChild(rateSlider);
		
		const rateValue = document.createElement('span');
		rateValue.textContent = '12 BPM';
		rateValue.style.color = '#4ecdc4';
		rateValue.style.fontSize = '0.8em';
		rateValue.id = 'rate-value';
		rateSliderDiv.appendChild(rateValue);
		
		breathingControlDiv.appendChild(rateSliderDiv);
		
		// Lung capacity adjustment
		const capacitySliderDiv = document.createElement('div');
		capacitySliderDiv.style.display = 'flex';
		capacitySliderDiv.style.alignItems = 'center';
		capacitySliderDiv.style.gap = '8px';
		capacitySliderDiv.style.marginTop = '4px';
		
		const capacityLabel = document.createElement('span');
		capacityLabel.textContent = 'Capacity:';
		capacityLabel.style.color = '#fff';
		capacityLabel.style.fontSize = '0.8em';
		capacitySliderDiv.appendChild(capacityLabel);
		
		const capacitySlider = document.createElement('input');
		capacitySlider.type = 'range';
		capacitySlider.min = '30';
		capacitySlider.max = '100';
		capacitySlider.value = '80';
		capacitySlider.style.width = '100px';
		capacitySlider.setAttribute('aria-label', 'Lung capacity control');
		capacitySlider.oninput = (e) => {
			const capacity = parseInt((e.target as HTMLInputElement).value) / 100;
			this.network.breathingSystem.adjustLungCapacity(capacity);
		};
		capacitySliderDiv.appendChild(capacitySlider);
		
		const capacityValue = document.createElement('span');
		capacityValue.textContent = '80%';
		capacityValue.style.color = '#4ecdc4';
		capacityValue.style.fontSize = '0.8em';
		capacityValue.id = 'capacity-value';
		capacitySliderDiv.appendChild(capacityValue);
		
		breathingControlDiv.appendChild(capacitySliderDiv);
		
		// Breathing sound volume control
		const volumeSliderDiv = document.createElement('div');
		volumeSliderDiv.style.display = 'flex';
		volumeSliderDiv.style.alignItems = 'center';
		volumeSliderDiv.style.gap = '8px';
		volumeSliderDiv.style.marginTop = '4px';
		
		const volumeLabel = document.createElement('span');
		volumeLabel.textContent = 'Volume:';
		volumeLabel.style.color = '#fff';
		volumeLabel.style.fontSize = '0.8em';
		volumeSliderDiv.appendChild(volumeLabel);
		
		const volumeSlider = document.createElement('input');
		volumeSlider.type = 'range';
		volumeSlider.min = '0';
		volumeSlider.max = '100';
		volumeSlider.value = '10';
		volumeSlider.style.width = '100px';
		volumeSlider.setAttribute('aria-label', 'Breathing sound volume control');
		volumeSlider.oninput = (e) => {
			const volume = parseInt((e.target as HTMLInputElement).value) / 100;
			this.setBreathingSoundVolume(volume);
		};
		volumeSliderDiv.appendChild(volumeSlider);
		
		const volumeValue = document.createElement('span');
		volumeValue.textContent = '10%';
		volumeValue.style.color = '#4ecdc4';
		volumeValue.style.fontSize = '0.8em';
		volumeValue.id = 'volume-value';
		volumeSliderDiv.appendChild(volumeValue);
		
		breathingControlDiv.appendChild(volumeSliderDiv);
		
		// Audit log display
		const auditLogBtn = document.createElement('button');
		auditLogBtn.textContent = '📊 Audit Log';
		auditLogBtn.style.padding = '6px 12px';
		auditLogBtn.style.fontSize = '0.8em';
		auditLogBtn.style.background = '#9b59b6';
		auditLogBtn.style.color = '#fff';
		auditLogBtn.style.border = 'none';
		auditLogBtn.style.borderRadius = '4px';
		auditLogBtn.style.cursor = 'pointer';
		auditLogBtn.style.marginTop = '8px';
		auditLogBtn.onclick = () => {
			this.showAuditLog();
		};
		breathingControlDiv.appendChild(auditLogBtn);

		// Voice parameter mini-panel
		const voicePanel = document.createElement('div');
		voicePanel.style.marginTop = '10px';
		voicePanel.style.paddingTop = '10px';
		voicePanel.style.borderTop = '1px solid #333';
		voicePanel.style.color = '#fff';
		voicePanel.innerHTML = '<strong>Voice</strong>';
		const mkSlider = (label: string, min: number, max: number, value: number, onInput: (v: number)=>void) => {
			const wrap = document.createElement('div');
			wrap.style.display = 'flex';
			wrap.style.alignItems = 'center';
			wrap.style.gap = '8px';
			wrap.style.marginTop = '6px';
			const span = document.createElement('span');
			span.textContent = label;
			span.style.fontSize = '0.8em';
			const input = document.createElement('input');
			input.type = 'range';
			input.min = String(min);
			input.max = String(max);
			input.value = String(value);
			input.style.width = '110px';
			const val = document.createElement('span');
			val.style.fontSize = '0.8em';
			val.style.color = '#4ecdc4';
			val.textContent = String(value);
			input.oninput = (e) => {
				const v = parseFloat((e.target as HTMLInputElement).value);
				val.textContent = String(v);
				onInput(v);
			};
			wrap.appendChild(span);
			wrap.appendChild(input);
			wrap.appendChild(val);
			return wrap;
		};
		voicePanel.appendChild(mkSlider('Pitch', 50, 500, this.network.voiceCharacteristics.pitch, (v)=> this.network.setVoiceParams({ pitch: v })));
		voicePanel.appendChild(mkSlider('Breath', 0, 1, this.network.voiceCharacteristics.breathiness, (v)=> this.network.setVoiceParams({ breathiness: v })));
		voicePanel.appendChild(mkSlider('Rough', 0, 1, this.network.voiceCharacteristics.roughness, (v)=> this.network.setVoiceParams({ roughness: v })));
		breathingControlDiv.appendChild(voicePanel);

		document.body.appendChild(breathingControlDiv);

		// Update breathing control panel displays
		this.updateBreathingDisplays();

		// Voice control methods
		this.cyclePersonality = () => {
			const personalities = ['warm', 'cold', 'energetic', 'calm', 'mysterious', 'playful', 'wise', 'youthful'];
			const currentIndex = personalities.indexOf(this.network.voiceCharacteristics.personality);
			const newIndex = (currentIndex + 1) % personalities.length;
			this.network.setVoicePersonality(personalities[newIndex] as any);
			console.log(`[Voice] Personality changed to: ${personalities[newIndex]}`);
		};

		this.cycleEmotion = () => {
			const emotions = ['neutral', 'happy', 'sad', 'excited', 'contemplative', 'curious', 'confident', 'uncertain'];
			const currentIndex = emotions.indexOf(this.network.voiceCharacteristics.emotionalState);
			const newIndex = (currentIndex + 1) % emotions.length;
			this.network.setEmotionalState(emotions[newIndex] as any);
			console.log(`[Voice] Emotional state changed to: ${emotions[newIndex]}`);
		};




		this.rotation = { x: Math.PI / 5, y: Math.PI / 4 }; // Start at above-diagonal

		let isDragging = false, lastX = 0, lastY = 0;
		this.canvas.style.pointerEvents = 'auto'; // Enable mouse events on canvas

		this.canvas.addEventListener('mousedown', (e) => {
			isDragging = true;
			lastX = e.clientX;
			lastY = e.clientY;
		});
		window.addEventListener('mousemove', (e) => {
			if (isDragging) {
				const dx = e.clientX - lastX;
				const dy = e.clientY - lastY;
				this.rotation.y += dx * 0.01;
				this.rotation.x += dy * 0.01;
				this.rotation.x = Math.max(-Math.PI / 2 + 0.1, Math.min(Math.PI / 2 - 0.1, this.rotation.x));
				lastX = e.clientX;
				lastY = e.clientY;
			}
		});
		window.addEventListener('mouseup', () => { isDragging = false; });
		this.canvas.addEventListener('mouseleave', () => { isDragging = false; });

		// Remove forced fixed angle in render, allow user to control rotation

		// Add sun toggle button (only show if ModernUI is not active)
		const sunBtn = document.createElement('button');
		sunBtn.textContent = '☀️ Toggle Sun Energy';
		sunBtn.style.position = 'fixed';
		sunBtn.style.top = '24px';
		sunBtn.style.right = '32px';
		sunBtn.style.zIndex = '9999';
		sunBtn.style.fontSize = '1.2em';
		sunBtn.style.padding = '10px 24px';
		sunBtn.style.background = '#ffe066';
		sunBtn.style.color = '#222';
		sunBtn.style.border = 'none';
		sunBtn.style.borderRadius = '8px';
		sunBtn.style.boxShadow = '0 2px 8px #ffe06644, 0 0 24px #ffe06644';
		sunBtn.style.cursor = 'pointer';
		
		// Hide sun button if ModernUI is active (it's in the header now)
		if (this.useModernUI) {
			sunBtn.style.display = 'none';
		}
		
		document.body.appendChild(sunBtn);

		sunBtn.onclick = () => {
			this.network.toggleSun();
			this.showNotification(this.network.sunEnabled ? 'Sun energy enabled!' : 'Sun energy disabled!');
		};

		// --- Magic aura effect behind UI panels ---
		const magicAura = document.createElement('div');
		magicAura.id = 'magic-aura';
		magicAura.style.position = 'fixed';
		magicAura.style.top = '0';
		magicAura.style.left = '0';
		magicAura.style.width = '100vw';
		magicAura.style.height = '100vh';
		magicAura.style.pointerEvents = 'none';
		magicAura.style.zIndex = '5';
		magicAura.style.transition = 'opacity 0.6s';
		magicAura.style.opacity = '0.112358';
		document.body.appendChild(magicAura);

		// Animate the magic aura's color and opacity
		const animateMagicAura = () => {
			const t = Date.now() / 1200;
			const hue = 220 + Math.sin(t) * 80;
			const opacity = 0.45 + 0.15 * Math.sin(t / 2);
			magicAura.style.background = `radial-gradient(circle at 55% 18%, hsl(${hue},90%,70%,0.22) 0%, hsl(${(hue+60)%360},80%,60%,0.12) 60%, transparent 100%)`;
			magicAura.style.opacity = `${opacity}`;
			requestAnimationFrame(animateMagicAura);
		};
		animateMagicAura();

		// Creature voice info is now displayed in the sidebar

		// Initialize modern UI if enabled
		if (this.useModernUI) {
			try {
				this.modernUI = new ModernUI(this.network);
				// Keep the integrated stats dashboard visible in ModernUI mode.
				this.statsDiv.style.display = 'block';
				
				// Hide legacy control panels when ModernUI is active
				const legacyBreathingPanel = document.querySelector('[style*="bottom: 120px"][style*="right: 24px"]');
				if (legacyBreathingPanel) {
					(legacyBreathingPanel as HTMLElement).style.display = 'none';
				}
				
				const legacyVoiceControls = document.querySelector('[style*="bottom: 220px"][style*="left: 24px"]');
				if (legacyVoiceControls) {
					(legacyVoiceControls as HTMLElement).style.display = 'none';
				}
			} catch (error) {
				console.warn('[UI] Failed to initialize ModernUI, falling back to legacy UI:', error);
				this.useModernUI = false;
			}
		}
		
		// NOTE: Render is driven by App.runMainLoop() → cycleSimulationData() → ui.render()
		// No independent renderLoop — avoids double-rendering every frame.
	}

	// Get linguistic communication statistics
	getLinguisticStats() {
		const network = this.network;
		const totalElements = network.linguisticElements.size;
		const vowels = network.discoveredVowels.length;
		const consonants = network.discoveredConsonants.length;
		const syllables = network.discoveredSyllables.length;
		const words = network.discoveredWords.length;
		const complexity = network.linguisticComplexity;
		const successRate = network.communicationAttempts > 0 ? 
			(network.successfulCommunications / network.communicationAttempts) * 100 : 0;
		
		// Audio synthesis statistics
		const synthesizedElements = Array.from(network.linguisticElements.values())
			.filter(element => element.audioSynthesized).length;
		const synthesisRate = totalElements > 0 ? (synthesizedElements / totalElements) * 100 : 0;
		const avgSynthesisQuality = Array.from(network.linguisticElements.values())
			.filter(element => element.synthesisQuality)
			.reduce((sum, element) => sum + (element.synthesisQuality || 0), 0) / 
			Array.from(network.linguisticElements.values()).filter(element => element.synthesisQuality).length || 0;
		
		return {
			totalElements,
			vowels,
			consonants,
			syllables,
			words,
			complexity,
			successRate,
			synthesizedElements,
			synthesisRate,
			avgSynthesisQuality
		};
	}

	getNetworkBounds() {
		const xs = this.network.nodes.map(n => n.x);
		const ys = this.network.nodes.map(n => n.y);
		const minX = Math.min(...xs);
		const maxX = Math.max(...xs);
		const minY = Math.min(...ys);
		const maxY = Math.max(...ys);
		return { minX, maxX, minY, maxY };
	}

	getNetworkStats() {
		const nodes = this.network.nodes.length;
		const edges = this.network.edges.length;
		const waves = this.network.waves.filter(w => w.active).length;
		const avgEnergy = nodes > 0 ? this.network.nodes.reduce((sum, n) => sum + (n.energy || 0), 0) / nodes : 0;
		const complexity = nodes > 0 ? Math.min(2.5, 1.0 + Math.log(nodes) * 0.1) : 1.0;
		
		return { nodes, edges, waves, avgEnergy, complexity };
	}

	/** Get current LOD/performance status for display/debugging */
	getLODStatus(): { tier: string; fps: number; degraded: boolean; frameSkip: number; lodLevel: number; dpr: number; dprReduced: boolean } {
		const state = this.lastLodState;
		return {
			tier: state?.tier ?? 'high',
			fps: state?.fps ?? 60,
			degraded: state?.isDegraded ?? false,
			frameSkip: state?.frameSkip ?? 1,
			lodLevel: this.visualEvolution?.getLODLevel() ?? 0,
			dpr: this.renderScaler?.currentDPR ?? 1,
			dprReduced: this.renderScaler?.currentDPR !== undefined && this.renderScaler.currentDPR < window.devicePixelRatio
		};
	}

	/** Force a specific quality tier (for testing/debugging) */
	forceQualityTier(tier: 'high' | 'medium' | 'low' | 'ultra' | 'extreme'): void {
		this.qualityManager?.forceTier(tier);
	}

	/** Get the quality manager instance (for external integration) */
	getQualityManager(): QualityTierManager {
		return this.qualityManager;
	}

	// Dynamically generate and display UI panels/widgets
	createPanel(id: string, options: { title?: string, content?: string, visible?: boolean, width?: string, height?: string, top?: string, right?: string, left?: string, bottom?: string }) {
		let panel = document.getElementById(id) as HTMLDivElement;
		if (!panel) {
			panel = document.createElement('div');
			panel.id = id;
			panel.style.position = 'fixed';
			panel.style.top = options.top ?? '160px'; // Updated to start below statistics bar
			panel.style.right = options.right ?? '0';
			if (options.left !== undefined) panel.style.left = options.left;
			if (options.bottom !== undefined) panel.style.bottom = options.bottom;
			panel.style.width = options.width ?? '320px';
			panel.style.height = options.height ?? 'calc(100vh - 160px)'; // Updated for new layout
			panel.style.background = 'rgba(49, 49, 49, 0.6)';
			panel.style.borderRadius = '12px 0 0 12px';
			panel.style.boxShadow = '-2px 0 8px #45027822';
			panel.style.padding = '18px 24px 18px 18px';
			panel.style.zIndex = '11';
			panel.style.maxHeight = 'calc(100vh - 160px)'; // Updated for new layout
			panel.style.overflowY = 'auto';
			panel.style.color = '#fff'; // <-- ensure white text for panels/tooltips
			document.body.appendChild(panel);
		}
		panel.style.display = options.visible === false ? 'none' : 'block';
		panel.innerHTML = options.title ? `<b>${options.title}</b><br>${options.content ?? ''}` : (options.content ?? '');
	}

	// Compact, non-blocking node hover tooltip near cursor
	showNodeTooltip(node: Node | null) {
		let tip = document.getElementById('node-tooltip') as HTMLDivElement;
		if (!tip) {
			tip = document.createElement('div');
			tip.id = 'node-tooltip';
			tip.style.position = 'fixed';
			tip.style.background = 'rgba(49,49,49,0.85)';
			tip.style.color = '#fff';
			tip.style.borderRadius = '6px';
			tip.style.padding = '6px 8px';
			tip.style.fontSize = '12px';
			tip.style.lineHeight = '1.2';
			tip.style.pointerEvents = 'none';
			tip.style.boxShadow = '0 2px 6px #0004';
			tip.style.zIndex = '6';
			document.body.appendChild(tip);
		}

		if (!node) {
			tip.style.display = 'none';
			return;
		}

		// Minimal content with zodiac and energy details
		const hueMatch = node.color.match(/hsl\((\d+)/);
		const hue = hueMatch ? parseInt(hueMatch[1]) : -1;
		let zodiac = 'unknown';
		if (hue >= 0) {
			if (hue >= 60 && hue < 80) zodiac = 'Taurus';
			else if (hue >= 80 && hue < 100) zodiac = 'Capricorn';
			else if (hue >= 100 && hue < 120) zodiac = 'Scorpio';
			else if (hue >= 120 && hue < 150) zodiac = 'Cancer';
			else if (hue >= 60 && hue < 150) zodiac = 'Virgo';
			else if (hue >= 150 && hue < 200) zodiac = 'Gemini';
			else if (hue >= 200 && hue < 260) zodiac = 'Aquarius';
			else if (hue >= 260 && hue < 320) zodiac = 'Pisces';
			else if (hue >= 320 && hue < 340) zodiac = 'Libra';
			else if (hue >= 40 && hue < 60) zodiac = 'Sagittarius';
			else if (hue >= 20 && hue < 40) zodiac = 'Leo';
			else if (hue < 20 || hue >= 340) zodiac = 'Aries';
		}
		const zodiacToElement: Record<string, string> = {
			'Aries': 'Fire','Leo': 'Fire','Sagittarius': 'Fire',
			'Taurus': 'Earth','Virgo': 'Earth','Capricorn': 'Earth',
			'Gemini': 'Air','Libra': 'Air','Aquarius': 'Air',
			'Cancer': 'Water','Scorpio': 'Water','Pisces': 'Water'
		};
		const element = zodiacToElement[zodiac] || '—';
		const energyStore = typeof node.energyStorage === 'number' ? node.energyStorage : 0;
		tip.innerHTML = `
			<div>#${node.id} · <span style="color:${node.color};font-weight:bold;">●</span> ${zodiac} <span style="opacity:0.8">(${element})</span></div>
			<div style="opacity:0.9;">E ${node.energy.toFixed(1)} | S ${energyStore.toFixed(1)} | V ${node.value.toFixed(1)}</div>
		`;
		tip.style.display = 'block';

		// Position near cursor; map canvas coords to CSS pixels
		if (this.mouse) {
			const rect = this.canvas.getBoundingClientRect();
			const scaleX = rect.width / this.canvas.width;
			const scaleY = rect.height / this.canvas.height;
			const cssX = rect.left + this.mouse.x * (scaleX || 1);
			const cssY = rect.top + this.mouse.y * (scaleY || 1);
			const offset = 12;
			let left = cssX + offset;
			let top = cssY + offset;
			// Keep inside viewport
			const maxLeft = window.innerWidth - 8 - tip.offsetWidth;
			const maxTop = window.innerHeight - 8 - tip.offsetHeight;
			left = Math.min(left, Math.max(8, maxLeft));
			top = Math.min(top, Math.max(8, maxTop));
			tip.style.left = `${left}px`;
			tip.style.top = `${top}px`;
		}
	}

	// Register custom widgets/panels for extensibility
	customPanels: Record<string, HTMLDivElement> = {};

	registerPanel(id: string, options: { title?: string, content?: string, top?: string, right?: string, width?: string, height?: string }) {
		let panel = document.getElementById(id) as HTMLDivElement;
		if (!panel) {
			panel = document.createElement('div');
			panel.id = id;
			panel.style.position = 'fixed';
			panel.style.top = options.top ?? '160px'; // Updated to start below statistics bar
			panel.style.right = options.right ?? '0';
			panel.style.width = options.width ?? '320px';
			panel.style.height = options.height ?? '';
			panel.style.background = '#fff9';
			panel.style.borderRadius = '12px 0 0 12px';
			panel.style.boxShadow = '-2px 0 8px #45027822';
			panel.style.padding = '18px 24px 18px 18px';
			panel.style.zIndex = '11';
			panel.style.maxHeight = 'calc(100vh - 160px)'; // Updated for new layout
			panel.style.overflowY = 'auto';
			panel.innerHTML = options.title ? `<b>${options.title}</b><br>${options.content ?? ''}` : (options.content ?? '');
			document.body.appendChild(panel);
		}
		this.customPanels[id] = panel;
		return panel;
	}

	updatePanel(id: string, options: { title?: string, content?: string, visible?: boolean }) {
		const panel = this.customPanels[id] || document.getElementById(id);
		if (!panel) return;
		panel.style.display = options.visible === false ? 'none' : 'block';
		panel.innerHTML = options.title ? `<b>${options.title}</b><br>${options.content ?? ''}` : (options.content ?? '');
	}


	// Simple event system for UI state changes
	private listeners: Record<string, Function[]> = {};

	on(event: string, handler: Function) {
		if (!this.listeners[event]) this.listeners[event] = [];
		this.listeners[event].push(handler);
	}

	emit(event: string, data?: any) {
		if (this.listeners[event]) {
			for (const handler of this.listeners[event]) {
				handler(data);
			}
		}
	}

	// Display contextual notifications
	showNotification(message: string, duration: number = 2500) {
		const notif = document.createElement('div');
		notif.style.position = 'fixed';
		notif.style.bottom = '32px';
		notif.style.right = '32px';
		notif.style.background = '#450278';
		notif.style.color = '#fff';
		notif.style.padding = '12px 24px';
		notif.style.borderRadius = '8px';
		notif.style.boxShadow = '0 2px 8px #45027822';
		notif.style.zIndex = '999';
		notif.style.fontSize = '1.1em';
		notif.textContent = message;
		document.body.appendChild(notif);
		setTimeout(() => notif.remove(), duration);
	}

	// Map node color (HSL) to sound properties
	private colorToSoundProps(color: string) {
		const hueMatch = color.match(/hsl\((\d+)/);
		const hue = hueMatch ? parseInt(hueMatch[1]) : 0;
		
		// Get base frequency from hue (zodiac-based)
		let baseFrequency = 220, waveform = 'sine', pan = 0;
		if (hue < 30) { baseFrequency = 220; waveform = 'sawtooth'; pan = -0.25; } // Red/Fire - A3
		else if (hue < 60) { baseFrequency = 246.94; waveform = 'square'; pan = -0.2; } // Orange - B3
		else if (hue < 90) { baseFrequency = 261.63; waveform = 'triangle'; pan = -0.15; } // Yellow/Earth - C4
		else if (hue < 120) { baseFrequency = 277.18; waveform = 'triangle'; pan = -0.1; } // Yellow-Green - C#4
		else if (hue < 150) { baseFrequency = 293.66; waveform = 'sine'; pan = -0.05; } // Green/Air - D4
		else if (hue < 180) { baseFrequency = 311.13; waveform = 'sine'; pan = 0; } // Cyan - D#4
		else if (hue < 210) { baseFrequency = 329.63; waveform = 'sine'; pan = 0.05; } // Blue/Water - E4
		else if (hue < 240) { baseFrequency = 349.23; waveform = 'sine'; pan = 0.1; } // Blue-Violet - F4
		else if (hue < 270) { baseFrequency = 369.99; waveform = 'triangle'; pan = 0.15; } // Violet/Spirit - F#4
		else if (hue < 300) { baseFrequency = 392.00; waveform = 'square'; pan = 0.2; } // Magenta - G4
		else if (hue < 330) { baseFrequency = 415.30; waveform = 'sawtooth'; pan = 0.25; } // Pink - G#4
		else { baseFrequency = 440.00; waveform = 'sine'; pan = 0.3; } // Rose/Warm - A4
		
		// Add harmonic interval for variation (minor 3rd, major 3rd, perfect 4th, perfect 5th)
		const intervals = [0, 3, 4, 5, 7, 12]; // Unison, minor 3rd, major 3rd, perfect 4th, perfect 5th, octave
		const intervalSemitones = intervals[Math.floor(Math.random() * intervals.length)];
		const frequency = baseFrequency * Math.pow(2, intervalSemitones / 12);
		
		return { frequency, waveform, pan };
	}

	// Enhanced buzz: stereo, layered, randomized pitch, sparkle for big growth
	private async playBuzz(frequency = 220, duration = 0.32, volume = 0.003, pan = 0, color?: string) {
		// ^ duration default changed from 0.18 to 0.32 for longer node sounds
		const ctx = this.network.audioContext;
		if (!ctx || ctx.state === 'closed' || ctx.state === 'suspended') return;
		let props = { frequency, waveform: 'square', pan };
		if (color) props = { ...props, ...this.colorToSoundProps(color) };
		
		// Try physical modeling first
		if (this.physicalSynthesizer) {
			try {
				const energy = Math.min(1.0, volume / 0.003); // Normalize energy
				const buffer = this.physicalSynthesizer.synthesizeUIInteraction(
					props.frequency,
					duration,
					energy,
					'buzz'
				);
				
				const source = ctx.createBufferSource();
				source.buffer = buffer;
				
				const gain = ctx.createGain();
				gain.gain.value = 0.0008; // Reduced to prevent clipping
				
				const panner = ctx.createStereoPanner();
				const panOffset = (Math.random() - 0.5) * 0.2;
				panner.pan.value = Math.max(-0.4, Math.min(0.4, props.pan + panOffset));
				
				if (this.reverbNode) {
					source.connect(gain);
					gain.connect(this.reverbNode);
					this.reverbNode.connect(panner);
				} else {
					source.connect(gain);
					gain.connect(panner);
				}
				const out = (this.network && (this.network as any).getAudioOutput) ? this.network.getAudioOutput() : ctx.destination;
				panner.connect(out);
				
				source.start(ctx.currentTime);
				
				this.buzzCountTotal++;
				return; // Success with physical modeling
			} catch (error) {
				console.warn('[UI Buzz] Physical modeling failed, using fallback:', error);
			}
		}
		
		// Fallback to original implementation
		const panOffset = (Math.random() - 0.5) * 0.2;
		const osc1 = ctx.createOscillator();
		const osc2 = ctx.createOscillator();
		const gain = ctx.createGain();
		const panner = ctx.createStereoPanner();
		osc1.type = props.waveform as OscillatorType;
		osc2.type = 'triangle';
		osc1.frequency.value = props.frequency + Math.random() * 60;
		osc2.frequency.value = props.frequency * 2 + Math.random() * 80;
		gain.gain.value = volume;
		panner.pan.value = Math.max(-0.4, Math.min(0.4, props.pan + panOffset));
		osc1.connect(gain);
		osc2.connect(gain);
		if (this.reverbNode) {
			gain.connect(this.reverbNode);
			this.reverbNode.connect(panner);
		} else {
			gain.connect(panner);
		}
		const __out1 = (this.network && (this.network as any).getAudioOutput) ? this.network.getAudioOutput() : ctx.destination;
		panner.connect(__out1);
		osc1.start();
		osc2.start();
		osc1.stop(ctx.currentTime + duration);
		osc2.stop(ctx.currentTime + duration);
		gain.gain.setValueAtTime(volume, ctx.currentTime);
		gain.gain.linearRampToValueAtTime(0, ctx.currentTime + duration);
		osc1.onended = () => {
			osc1.disconnect();
			osc2.disconnect();
			gain.disconnect();
			panner.disconnect();
		};
		this.buzzCountTotal++;
	}

	// Sparkle sound for large growth events
	private async playSparkle(volume = 0.0025, color?: string) {
		const ctx = this.network.audioContext;
		if (!ctx || ctx.state === 'closed' || ctx.state === 'suspended') return;
		let props = { frequency: 1200, waveform: 'sine', pan: 0 };
		if (color) props = { ...props, ...this.colorToSoundProps(color) };
		
		// Try physical modeling first
		if (this.physicalSynthesizer) {
			try {
				const energy = Math.min(1.0, volume / 0.0025);
				const buffer = this.physicalSynthesizer.synthesizeUIInteraction(
					props.frequency,
					0.18,
					energy,
					'sparkle'
				);
				
				const source = ctx.createBufferSource();
				source.buffer = buffer;
				
				const gain = ctx.createGain();
				gain.gain.value = 0.0008; // Reduced to prevent clipping
				
				const panner = ctx.createStereoPanner();
				panner.pan.value = props.pan;
				
				if (this.reverbNode) {
					source.connect(gain);
					gain.connect(this.reverbNode);
					this.reverbNode.connect(panner);
				} else {
					source.connect(gain);
					gain.connect(panner);
				}
				const out2 = (this.network && (this.network as any).getAudioOutput) ? this.network.getAudioOutput() : ctx.destination;
				panner.connect(out2);
				
				source.start(ctx.currentTime);
				
				this.sparkleCountTotal++;
				return; // Success with physical modeling
			} catch (error) {
				console.warn('[UI Sparkle] Physical modeling failed, using fallback:', error);
			}
		}
		
		// Fallback to original implementation
		const osc = ctx.createOscillator();
		const gain = ctx.createGain();
		const panner = ctx.createStereoPanner();
		osc.type = props.waveform as OscillatorType;
		osc.frequency.setValueAtTime(props.frequency, ctx.currentTime);
		osc.frequency.linearRampToValueAtTime(props.frequency + 600, ctx.currentTime + 0.18);
		gain.gain.value = volume;
		panner.pan.value = props.pan;
		osc.connect(gain);
		if (this.reverbNode) {
			gain.connect(this.reverbNode);
			this.reverbNode.connect(panner);
		} else {
			gain.connect(panner);
		}
		const __outBuzz = (this.network && (this.network as any).getAudioOutput) ? this.network.getAudioOutput() : ctx.destination;
		panner.connect(__outBuzz);
		osc.start();
		osc.stop(ctx.currentTime + 0.18);
		gain.gain.setValueAtTime(volume, ctx.currentTime);
		gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.18);
		osc.onended = () => {
			osc.disconnect();
			gain.disconnect();
			panner.disconnect();
		};
		this.sparkleCountTotal++;
	}
	

	// Enhanced hum: layered, modulated, reverb, stereo
	private async startHeartHum(frequency?: number) {
		const ctx = this.network.audioContext;
		if (!ctx || ctx.state === 'closed' || ctx.state === 'suspended' || this.heartHumOsc) return;
		const osc1 = ctx.createOscillator();
		const gain = ctx.createGain();
		const panner = ctx.createStereoPanner();
		osc1.type = 'triangle';
		osc1.frequency.value = frequency ?? (52 + Math.random() * 8);
		gain.gain.value = 0.002; // Boosted for audibility while staying safe
		panner.pan.value = 0;
		osc1.connect(gain);
		if (this.reverbNode) {
			gain.connect(this.reverbNode);
			this.reverbNode.connect(panner);
		} else {
			gain.connect(panner);
		}
		const __outTwin = (this.network && (this.network as any).getAudioOutput) ? this.network.getAudioOutput() : ctx.destination;
		panner.connect(__outTwin);
		osc1.start();
		gain.gain.setValueAtTime(0, ctx.currentTime);
		gain.gain.linearRampToValueAtTime(0.0015, ctx.currentTime + 2.5); // Smooth fade-in to audible level
		setTimeout(() => {
			if (this.heartHumGain) {
				const now = ctx.currentTime;
				this.heartHumGain.gain.linearRampToValueAtTime(0, now + 2.5);
			}
		}, 30000);
		this.heartHumOsc = osc1;
		this.heartHumGain = gain;
		(this as any).heartHumPanner = panner;
		this.heartHumStartCount++;
	}

	private stopHeartHum() {
		if (this.heartHumOsc) {
			this.heartHumOsc.stop();
			this.heartHumOsc.disconnect();
			this.heartHumOsc = null;
		}
		if (this.heartHumGain) {
			this.heartHumGain.disconnect();
			this.heartHumGain = null;
		}
		if ((this as any).heartHumPanner) {
			(this as any).heartHumPanner.disconnect();
			(this as any).heartHumPanner = null;
		}
	}

	// 3D projection utility
	project3D(x: number, y: number, z: number, rotation: { x: number, y: number }, scale: number, offsetX: number, offsetY: number) {
		// Rotate around Y axis
		let dx = x, dy = y, dz = z;
		const cosY = Math.cos(rotation.y), sinY = Math.sin(rotation.y);
		const cosX = Math.cos(rotation.x), sinX = Math.sin(rotation.x);
		// Y rotation
		let px = dx * cosY - dz * sinY;
		let pz = dx * sinY + dz * cosY;
		// X rotation
		let py = dy * cosX - pz * sinX;
		pz = dy * sinX + pz * cosX;
		// Orthographic projection
		return {
			x: px * scale + offsetX,
			y: py * scale + offsetY
		};
	}

	// Breathing sound control methods
	toggleBreathingSounds(): void {
		const button = document.querySelector('button[onclick*="toggleBreathingSounds"]') as HTMLButtonElement;
		if (!button) return;
		
		// Toggle breathing sounds on/off
		if (button.textContent?.includes('🔊')) {
			button.textContent = '🔇 Breathing Sounds';
			button.style.background = '#666';
			this.disableBreathingSounds();
		} else {
			button.textContent = '🔊 Breathing Sounds';
			button.style.background = '#ff6b9d';
			this.enableBreathingSounds();
		}
	}

	enableBreathingSounds(): void {
		// Enable breathing sounds
		if (this.network.breathingSystem) {
			this.network.breathingSystem.setBreathingSoundEnabled(true);
			console.log('[Breathing] Sounds enabled');
		}
	}

	disableBreathingSounds(): void {
		// Disable breathing sounds
		if (this.network.breathingSystem) {
			this.network.breathingSystem.setBreathingSoundEnabled(false);
			console.log('[Breathing] Sounds disabled');
		}
	}

	setBreathingSoundVolume(volume: number): void {
		// Set breathing sound volume
		if (this.network.breathingSystem) {
			this.network.breathingSystem.setBreathingSoundVolume(volume);
		}
		
		// Update volume display
		const volumeValue = document.getElementById('volume-value');
		if (volumeValue) {
			volumeValue.textContent = `${(volume * 100).toFixed(0)}%`;
		}
	}

	showAuditLog(): void {
		// Create audit log modal
		const modal = document.createElement('div');
		modal.style.position = 'fixed';
		modal.style.top = '0';
		modal.style.left = '0';
		modal.style.width = '100%';
		modal.style.height = '100%';
		modal.style.background = 'rgba(0, 0, 0, 0.8)';
		modal.style.zIndex = '10000';
		modal.style.display = 'flex';
		modal.style.justifyContent = 'center';
		modal.style.alignItems = 'center';
		
		const logDiv = document.createElement('div');
		logDiv.style.background = '#1a1a1a';
		logDiv.style.border = '2px solid #4ecdc4';
		logDiv.style.borderRadius = '8px';
		logDiv.style.padding = '20px';
		logDiv.style.maxWidth = '80%';
		logDiv.style.maxHeight = '80%';
		logDiv.style.overflow = 'auto';
		logDiv.style.color = '#fff';
		logDiv.style.fontFamily = 'monospace';
		logDiv.style.fontSize = '12px';
		
		// Get audit logs from both systems
		const breathingLog = this.network.breathingSystem.getAuditLog();
		const voiceLog = this.network.creatureVoice.getVoiceAuditLog();
		
		// Combine and sort logs
		const allLogs = [...breathingLog, ...voiceLog].sort((a, b) => a.timestamp - b.timestamp);
		
		// Display logs
		logDiv.innerHTML = `
			<h3 style="color: #4ecdc4; margin-top: 0;">System Audit Log</h3>
			<div style="margin-bottom: 10px;">
				<strong>Breathing System:</strong> ${breathingLog.length} entries<br>
				<strong>Voice System:</strong> ${voiceLog.length} entries<br>
				<strong>Total:</strong> ${allLogs.length} entries
			</div>
			<div style="max-height: 400px; overflow-y: auto; border: 1px solid #333; padding: 10px; background: #000;">
				${allLogs.slice(-100).map(entry => {
					const time = new Date(entry.timestamp).toLocaleTimeString();
					const duration = entry.duration ? ` (${entry.duration.toFixed(2)}ms)` : '';
					return `<div style="margin-bottom: 2px; color: #${entry.system === 'BREATHING_SYSTEM' ? '4ecdc4' : 'ff6b9d'};">[${time}] ${entry.system}.${entry.action}: ${entry.details}${duration}</div>`;
				}).join('')}
			</div>
			<div style="margin-top: 10px;">
				<button onclick="this.parentElement.parentElement.parentElement.remove()" style="padding: 8px 16px; background: #4ecdc4; color: #000; border: none; border-radius: 4px; cursor: pointer;">Close</button>
				<button onclick="this.showAllLogs()" style="padding: 8px 16px; background: #ff6b9d; color: #fff; border: none; border-radius: 4px; cursor: pointer; margin-left: 10px;">Show All</button>
			</div>
		`;
		
		modal.appendChild(logDiv);
		document.body.appendChild(modal);
		
		// Close on click outside
		modal.onclick = (e) => {
			if (e.target === modal) {
				modal.remove();
			}
		};
	}

	getStageEmoji(stage: string): string {
		const stageEmojis: { [key: string]: string } = {
			'embryonic': '🥚',
			'fetal': '👶',
			'newborn': '🍼',
			'infant': '👶',
			'child': '🧒',
			'adolescent': '👦',
			'adult': '👨',
			'master': '🧙'
		};
		return stageEmojis[stage] || '❓';
	}

	updateBreathingDisplays(): void {
		const breathingState = this.network.breathingSystem.getBreathingState();
		const breathingThresholds = this.network.breathingSystem.getVoiceCapacityThresholds();
		
		const oxygenDiv = document.getElementById('oxygen-display');
		const co2Div = document.getElementById('co2-display');
		const voiceCapacityDiv = document.getElementById('voice-capacity-display');
		const rateValue = document.getElementById('rate-value');
		const capacityValue = document.getElementById('capacity-value');
		const developmentDiv = document.getElementById('breathing-development-display');
		
		if (oxygenDiv) {
			const pct = (breathingState.oxygenLevel * 100).toFixed(0);
			const color = breathingState.oxygenLevel > 0.7 ? '#4ecdc4' : breathingState.oxygenLevel > 0.4 ? '#ffd93d' : '#ff6b6b';
			oxygenDiv.innerHTML = `O₂: <span style="color: ${color}">${pct}%</span>`;
		}
		if (co2Div) {
			const pct = (breathingState.carbonDioxideLevel * 100).toFixed(0);
			const color = breathingState.carbonDioxideLevel < 0.3 ? '#4ecdc4' : breathingState.carbonDioxideLevel < 0.6 ? '#ffd93d' : '#ff6b6b';
			co2Div.innerHTML = `CO₂: <span style="color: ${color}">${pct}%</span>`;
		}
		if (voiceCapacityDiv) {
			const pct = (breathingThresholds.maxIntensity * 100).toFixed(0);
			const color = breathingThresholds.canSpeak ? '#4ecdc4' : '#ff6b6b';
			const vStatus = breathingState.voiceUnlocked ? 'UNLOCKED' : 'LOCKED';
			const vColor = breathingState.voiceUnlocked ? '#4ecdc4' : '#ff6b6b';
			voiceCapacityDiv.innerHTML = `Voice: <span style="color: ${vColor}">${vStatus}</span> <span style="color: ${color}">${pct}%</span> <span style="color: #ffd93d;">🫁</span>`;
		}
		if (developmentDiv) {
			const masteryPct = (breathingState.breathingMastery * 100).toFixed(0);
			const emoji = this.getStageEmoji(breathingState.breathingDevelopmentStage);
			const lessonInfo = breathingState.currentBreathingLesson ?
				`<br>Lesson: ${breathingState.currentBreathingLesson.name} (${(breathingState.currentBreathingLesson.progress * 100).toFixed(0)}%)` :
				'<br>All lessons completed!';
			developmentDiv.innerHTML = `<strong>${emoji} ${breathingState.breathingDevelopmentStage.toUpperCase()}</strong><br>Mastery: ${masteryPct}% | Lessons: ${breathingState.breathingLessonsLearned}${lessonInfo}`;
		}
		if (rateValue) rateValue.textContent = `${breathingState.respiratoryRate.toFixed(1)} BPM`;
		if (capacityValue) capacityValue.textContent = `${(breathingState.lungCapacity * 100).toFixed(0)}%`;
	}

	render() {
		if (!this.ctx) return;
		const pt = getPerformanceTrail();
		this.frameCount++;

		// ── LOD Quality Update (runs every frame per AstroMonix pattern) ──
		pt.start('render:lodUpdate');
		const rawFPS = pt.getAverageFps() || 60;
		const lodState = this.qualityManager.update(rawFPS);
		this.lastLodState = lodState;

		// Update render scaler
		const scalerState = this.renderScaler.update(
			lodState.scalars.targetDPR,
			lodState.frameSkip,
			false,
			true // scene is always changing in simulation
		);

		// Update throttler intervals from quality tier
		this.updateThrottler.setIntervals(lodState.updateIntervals);
		const throttleState = this.updateThrottler.tick();

		// Feed tier-based LOD level to visual evolution engine
		const { visualLODLevel } = computeSceneElementState(
			lodState.tier, lodState.scalars, throttleState.updateStats,
			false, lodState.doCatchup
		);
		this.visualEvolution.setExternalLODLevel(visualLODLevel);

		// Compute scene element LOD state
		const sceneLOD = computeSceneElementState(
			lodState.tier, lodState.scalars,
			throttleState.updateStats, false, lodState.doCatchup
		);
		pt.end('render:lodUpdate');

		// ── Frame Skip Check ──
		if (lodState.shouldSkipFrame) {
			return; // Skip render entirely this frame
		}

		// Apply DPR scaling to canvas if needed
		const dprChanged = this.renderScaler.applyToCanvas(
			this.canvas,
			window.innerWidth,
			window.innerHeight
		);
		if (dprChanged) {
			this.lastCanvasWidth = 0; // Force full redraw on DPR change
			this.lastCanvasHeight = 0;
		}

		// Update visual evolution engine
		pt.start('render:visEvo');
		const now = Date.now();
		const deltaTime = (now - this.lastFrameTime) / 1000;
		this.lastFrameTime = now;
		this.visualEvolution.update(deltaTime);
		pt.end('render:visEvo');

		// OPTIMIZATION: Only resize canvas when window size actually changes
		const currentWidth = window.innerWidth;
		const currentHeight = window.innerHeight;
		if (this.lastCanvasWidth !== currentWidth || this.lastCanvasHeight !== currentHeight) {
			this.canvas.width = currentWidth;
			this.canvas.height = currentHeight;
			this.lastCanvasWidth = currentWidth;
			this.lastCanvasHeight = currentHeight;
		}

		// Clear canvas
		this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

		// CRITICAL: Reset canvas state to prevent accumulation
		this.ctx.globalAlpha = 1.0;
		this.ctx.globalCompositeOperation = 'source-over';
		this.ctx.shadowBlur = 0;
		this.ctx.filter = 'none';

		// --- Camera angle: prefer above and diagonal, avoid pure side view ---
		// Instead of auto-rotating to maximize Z span, use a fixed diagonal-above angle
		// Example: rotate 35deg around X, 45deg around Y
		// this.rotation.x = Math.PI / 5; // ~36 degrees above
		// this.rotation.y = Math.PI / 4; // 45 degrees diagonal

		// Animate title to reflect growth
		this.titleDiv.textContent = `Neural Network Simulator (${this.network.nodes.length} nodes)`;
		this.titleDiv.style.color = `hsl(${220 + Math.min(80, this.network.nodes.length * 0.5)}, 60%, 40%)`;

		// Show node/edge count and evolution info with creature state
		const stateEmoji = {
			'growing': '🌱',
			'stabilizing': '⚖️',
			'sensing': '👁️',
			'exploring': '🔍'
		};
		
		const currentNodeCount = this.network.nodes.filter(node => !node.corroded).length;
		const nodeLimitReached = this.network.hasReachedNodeLimit;
		const nodeLimitIndicator = nodeLimitReached ? '🔒' : '🔓';
		
		// Linguistic communication stats
		pt.start('render:stats');
		const linguisticStats = this.getLinguisticStats();
		
		const archetypeSummary = (() => {
			const m = this.network.archetypeMetrics;
			if (!m || !m.top || m.top.length === 0) return '<i>Computing...</i>';
			return m.top
				.map(a => `${a.name} <b>${Math.round(a.score * 100)}%</b>`)
				.join(' | ');
		})();

		// Verbose evolving statistics
		const avgEnergy = this.network.nodes.length > 0 ? (this.network.nodes.reduce((sum, n) => sum + n.energy, 0) / this.network.nodes.length).toFixed(3) : '0';
		const avgValue = this.network.nodes.length > 0 ? (this.network.nodes.reduce((sum, n) => sum + n.value, 0) / this.network.nodes.length).toFixed(3) : '0';
		const growthRate = (this.network.nodes.length / Math.max(1, Math.floor(this.network.step / this.network.evolutionInterval))).toFixed(2);
		const pulseInterval = this.network.pulseInterval;
		const evolutionInterval = this.network.evolutionInterval;
		const lastPulseStep = Math.floor(this.network.step / pulseInterval) * pulseInterval;
		const nextPulseStep = lastPulseStep + pulseInterval;
		const lastEvolutionStep = Math.floor(this.network.step / evolutionInterval) * evolutionInterval;
		const nextEvolutionStep = lastEvolutionStep + evolutionInterval;

		// Get creature voice statistics
		const voiceState = this.network.creatureVoice.getCurrentVoiceState();
		const voiceLinguisticStats = this.getLinguisticStats();
		
		// Get breathing system statistics
		const breathingState = this.network.breathingSystem.getBreathingState();
		const breathingThresholds = this.network.breathingSystem.getVoiceCapacityThresholds();
		const breathingHealth = this.network.breathingSystem.getHealthStatus();

		// LOD/Performance status
		const lodStatus = this.getLODStatus();
		const lodColor = lodStatus.degraded ? '#f59e0b' : '#10b981';
		const tierColors: Record<string, string> = {
			high: '#10b981', medium: '#22c55e', low: '#f59e0b', ultra: '#f97316', extreme: '#ef4444'
		};

		// ── Stats panel: skip heavy computation + DOM at throttled rates ──
		if (sceneLOD.updateStatsPanel) {
		this.statsDiv.innerHTML = `
			<div style="font-size:1.02em;font-weight:bold;margin-bottom:10px;">Simulation Dashboard</div>
			<div style="margin-bottom:12px;padding:10px;border-radius:8px;background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.08);">
				<div style="font-weight:bold;margin-bottom:6px;">Core State</div>
				<div>Nodes: <b>${currentNodeCount}</b>/<b>${(this.network as any).getCurrentNodeLimit?.() ?? this.network.maxNodesPerCreature ?? 369}</b> ${nodeLimitIndicator}</div>
				<div>Edges: <b>${this.network.edges.length}</b></div>
				<div>Evolution Step: <b>${Math.floor(this.network.step / this.network.evolutionInterval)}</b></div>
				<div>State: <b>${stateEmoji[this.network.creatureState]} ${this.network.creatureState.toUpperCase()}</b></div>
				<div>Energy: <b>${this.network.totalNetworkEnergy?.toFixed(1) || '0'}</b></div>
			</div>

			<div style="margin-bottom:12px;padding:10px;border-radius:8px;background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.08);">
				<div style="font-weight:bold;margin-bottom:6px;">⚡ Performance</div>
				<div>FPS: <b style="color:${lodColor}">${lodStatus.fps}</b></div>
				<div>Quality: <b style="color:${tierColors[lodStatus.tier] || '#fff'}">${lodStatus.tier.toUpperCase()}</b></div>
				<div>LOD Level: <b>${lodStatus.lodLevel}</b> | Frame Skip: <b>${lodStatus.frameSkip}x</b></div>
				<div>DPR: <b>${lodStatus.dpr.toFixed(1)}</b>${lodStatus.dprReduced ? ' <span style="color:#f97316">(reduced)</span>' : ''}</div>
			</div>

			<div style="margin-bottom:12px;padding:10px;border-radius:8px;background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.08);">
				<div style="font-weight:bold;margin-bottom:6px;">☀️ Celestial</div>
				<div>Phase: <b>${this.network.celestialPhase?.toUpperCase() || '—'}</b> ${this.network.isNight ? '🌙' : this.network.isTwilight ? '🌅' : '☀️'}</div>
				<div>Light: <b>${(this.network.ambientLight * 100).toFixed(0)}%</b></div>
				<div>Sun: <b>${this.network.sunEnabled ? 'Active' : 'Inactive'}</b></div>
			</div>

			<div style="margin-bottom:12px;padding:10px;border-radius:8px;background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.08);">
				<div style="font-weight:bold;margin-bottom:6px;">Language</div>
				<div>Elements: <b>${linguisticStats.totalElements}</b></div>
				<div>Vowels: <b>${linguisticStats.vowels}</b> | Consonants: <b>${linguisticStats.consonants}</b></div>
				<div>Syllables: <b>${linguisticStats.syllables}</b> | Words: <b>${linguisticStats.words}</b></div>
				<div>Complexity: <b>${linguisticStats.complexity.toFixed(1)}</b></div>
				<div>Success Rate: <b>${linguisticStats.successRate.toFixed(1)}%</b></div>
			</div>

			<div style="margin-bottom:12px;padding:10px;border-radius:8px;background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.08);">
				<div style="font-weight:bold;margin-bottom:6px;">Audio</div>
				<div>Synthesized: <b>${linguisticStats.synthesizedElements}</b></div>
				<div>Synthesis Rate: <b>${linguisticStats.synthesisRate.toFixed(1)}%</b></div>
				<div>Quality: <b>${linguisticStats.avgSynthesisQuality.toFixed(2)}</b></div>
				<div>Voice Pitch: <b>${this.network.voiceCharacteristics.pitch.toFixed(0)}Hz</b></div>
				<div>Status: <b>${this.network.audioContext?.state === 'running' ? 'Active' : 'Waiting for interaction'}</b></div>
			</div>

			<div style="margin-bottom:12px;padding:10px;border-radius:8px;background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.08);">
				<div style="font-weight:bold;margin-bottom:6px;">Archetypes</div>
				<div>${archetypeSummary}</div>
			</div>

			<b>Verbose Statistics</b><br>
			<ul style="margin:8px 0 0 16px;padding:0;">
				<li><b>Node Count:</b> ${this.network.nodes.length}</li>
				<li><b>Edge Count:</b> ${this.network.edges.length}</li>
				<li><b>Average Node Energy:</b> ${avgEnergy}</li>
				<li><b>Average Node Value:</b> ${avgValue}</li>
				<li><b>Growth Rate (nodes/evolution):</b> ${growthRate}</li>
				<li><b>Pulse Interval:</b> ${pulseInterval} steps</li>
				<li><b>Next Pulse:</b> step ${nextPulseStep}</li>
				<li><b>Evolution Interval:</b> ${evolutionInterval} steps</li>
				<li><b>Next Evolution:</b> step ${nextEvolutionStep}</li>
				<li><b>Current Simulation Step:</b> ${this.network.step}</li>
			</ul>
			<b>🧠 Creature Voice</b><br>
			<ul style="margin:8px 0 0 16px;padding:0;">
				<li><b>Growth Phase:</b> ${voiceState.growthPhase}</li>
				<li><b>Emotion:</b> ${voiceState.currentEmotion}</li>
				<li><b>Energy Level:</b> ${(voiceState.energyLevel * 100).toFixed(0)}%</li>
				<li><b>Vocal Maturity:</b> ${(voiceState.vocalMaturity * 100).toFixed(0)}%</li>
				<li><b>Voice Evolution:</b> ${(voiceState.voiceEvolution * 100).toFixed(0)}%</li>
				<li><b>Discovery Excitement:</b> ${(voiceState.discoveryExcitement * 100).toFixed(0)}%</li>
				<li><b>Connection Harmony:</b> ${(voiceState.connectionHarmony * 100).toFixed(0)}%</li>
			</ul>
			<b>🫁 Breathing System</b><br>
			<ul style="margin:8px 0 0 16px;padding:0;">
				<li><b>Oxygen Level:</b> ${(breathingState.oxygenLevel * 100).toFixed(0)}%</li>
				<li><b>CO2 Level:</b> ${(breathingState.carbonDioxideLevel * 100).toFixed(0)}%</li>
				<li><b>Lung Capacity:</b> ${(breathingState.lungCapacity * 100).toFixed(0)}%</li>
				<li><b>Respiratory Rate:</b> ${breathingState.respiratoryRate.toFixed(1)} BPM</li>
				<li><b>Cycle Phase:</b> ${breathingState.cyclePhase}</li>
				<li><b>Voice Capacity:</b> ${(breathingState.voiceCapacity * 100).toFixed(0)}%</li>
				<li><b>Max Voice Duration:</b> ${breathingState.maxVoiceDuration.toFixed(0)}ms</li>
				<li><b>Voice Intensity Limit:</b> ${(breathingState.voiceIntensityLimit * 100).toFixed(0)}%</li>
				<li><b>Respiratory Efficiency:</b> ${(breathingState.respiratoryEfficiency * 100).toFixed(0)}%</li>
				<li><b>Fatigue Level:</b> ${(breathingState.fatigueLevel * 100).toFixed(0)}%</li>
				<li><b>Stress Level:</b> ${(breathingState.stressLevel * 100).toFixed(0)}%</li>
				<li><b>Health Status:</b> ${breathingHealth.status.toUpperCase()} - ${breathingHealth.message}</li>
				<li><b>Can Speak:</b> ${breathingThresholds.canSpeak ? 'YES' : 'NO'}</li>
				<li><b>Breath Required:</b> ${breathingThresholds.breathRequired ? 'YES' : 'NO'}</li>
				<li><b>Voice Unlocked:</b> ${breathingState.voiceUnlocked ? 'YES' : 'NO'}</li>
			</ul>
			<b>🫁 Breathing Development</b><br>
			<ul style="margin:8px 0 0 16px;padding:0;">
				<li><b>Development Stage:</b> ${breathingState.breathingDevelopmentStage.toUpperCase()}</li>
				<li><b>Breathing Mastery:</b> ${(breathingState.breathingMastery * 100).toFixed(0)}%</li>
				<li><b>Lessons Learned:</b> ${breathingState.breathingLessonsLearned}</li>
				<li><b>Learned Efficiency:</b> ${(breathingState.breathingEfficiency * 100).toFixed(0)}%</li>
				<li><b>Current Lesson:</b> ${breathingState.currentBreathingLesson ? breathingState.currentBreathingLesson.name : 'None'}</li>
				<li><b>Lesson Progress:</b> ${breathingState.currentBreathingLesson ? (breathingState.currentBreathingLesson.progress * 100).toFixed(0) + '%' : 'N/A'}</li>
				<li><b>Instincts Learned:</b> ${breathingState.breathingInstincts.filter(i => i.learned).length}/${breathingState.breathingInstincts.length}</li>
			</ul>
			<b>🗣️ Language System</b><br>
			<ul style="margin:8px 0 0 16px;padding:0;">
				<li><b>Total Elements:</b> ${voiceLinguisticStats.totalElements}</li>
				<li><b>Vowels:</b> ${voiceLinguisticStats.vowels}</li>
				<li><b>Consonants:</b> ${voiceLinguisticStats.consonants}</li>
				<li><b>Syllables:</b> ${voiceLinguisticStats.syllables}</li>
				<li><b>Words:</b> ${voiceLinguisticStats.words}</li>
				<li><b>Success Rate:</b> ${voiceLinguisticStats.successRate.toFixed(1)}%</li>
				<li><b>Synthesis Rate:</b> ${voiceLinguisticStats.synthesisRate.toFixed(1)}%</li>
			</ul>
			<b>Sound Statistics</b><br>
			<ul style="margin:8px 0 0 16px;padding:0;">
				<li><b>Buzz Sounds Played:</b> ${this.buzzCountTotal}</li>
				<li><b>Sparkle Sounds Played:</b> ${this.sparkleCountTotal}</li>
				<li><b>Heart Hum Started:</b> ${this.heartHumStartCount}</li>
			</ul>
		`;
		} // end if sceneLOD.updateStatsPanel
		pt.end('render:stats');

		pt.start('render:projection');
		// Use only valid nodes for rendering
		const nodesToRender: Node[] = typeof this.network.getRenderableNodes === 'function'
			? this.network.getRenderableNodes()
			: this.network.nodes;

		// ── Frame interpolation: use lerped positions between previous and current sim state ──
		// getInterpolatedPosition uses network.interpolationAlpha (set by App.runMainLoop)
		// alpha=0 → previous state; alpha=1 → current state; 0<alpha<1 → smooth in-between
		const interpolatedPositions: { x: number; y: number; z: number }[] = [];
		const hasInterpolation = typeof this.network.getInterpolatedPosition === 'function';
		for (let i = 0; i < nodesToRender.length; i++) {
			const ipos = hasInterpolation ? this.network.getInterpolatedPosition(i) : null;
			if (ipos) {
				interpolatedPositions.push(ipos);
			} else {
				const n = nodesToRender[i];
				interpolatedPositions.push({ x: n.x, y: n.y, z: n.z ?? 0 });
			}
		}

		// 3D: assign z if missing (on original nodes for simulation consistency)
		for (const node of nodesToRender) {
			if (typeof node.z !== 'number') node.z = (Math.random() - 0.5) * 120;
		}

		// Project all nodes to 2D using interpolated positions for smooth motion
		const projected = interpolatedPositions.map((pos) =>
			this.project3D(pos.x, pos.y, pos.z, this.rotation, 1, 0, 0)
		);

		// --- Z sorting for accurate 3D overlap (using interpolated positions) ---
		const projectedWithZ = interpolatedPositions.map((pos, i) => {
			const cosY = Math.cos(this.rotation.y), sinY = Math.sin(this.rotation.y);
			const cosX = Math.cos(this.rotation.x), sinX = Math.sin(this.rotation.x);
			let dx = pos.x, dy = pos.y, dz = pos.z;
			let px = dx * cosY - dz * sinY;
			let pz = dx * sinY + dz * cosY;
			let py = dy * cosX - pz * sinX;
			pz = dy * sinX + pz * cosX;
			return { node: nodesToRender[i], px, py, pz };
		});
		// Sort nodes by projected z (pz), farthest to nearest
		projectedWithZ.sort((a: { pz: number }, b: { pz: number }) => a.pz - b.pz);

		// Find projected bounds
		let minPX = Infinity, maxPX = -Infinity, minPY = Infinity, maxPY = -Infinity;
		for (const p of projectedWithZ) {
			minPX = Math.min(minPX, p.px);
			maxPX = Math.max(maxPX, p.px);
			minPY = Math.min(minPY, p.py);
			maxPY = Math.max(maxPY, p.py);
		}
		const projWidth = maxPX - minPX;
		const projHeight = maxPY - minPY;

		const margin = 80;
		const availableWidth = this.canvas.width - margin * 2;
		const availableHeight = this.canvas.height - margin * 2;
		const fitScale = Math.min(
			availableWidth / (projWidth || 1),
			availableHeight / (projHeight || 1),
			1.5
		) * 0.92;

		const offsetX = this.canvas.width / 2 - ((minPX + maxPX) / 2) * fitScale;
		const offsetY = this.canvas.height / 2 - ((minPY + maxPY) / 2) * fitScale;

		// ── Viewport Pre-Cull: filter projected nodes to only those visible on screen ──
		// This avoids expensive getNodeVisualProperties/getNodeVisualState calls for off-screen
		// nodes in the glow and shape rendering passes below. Uses a generous margin to account
		// for large node glows, particle effects, and edge connections.
		const cullMargin = 250;
		const cullLeft = -cullMargin;
		const cullRight = this.canvas.width + cullMargin;
		const cullTop = -cullMargin;
		const cullBottom = this.canvas.height + cullMargin;
		const visibleProjected: { node: Node; px: number; py: number; pz: number; sx: number; sy: number }[] = [];
		for (const p of projectedWithZ) {
			const sx = p.px * fitScale + offsetX;
			const sy = p.py * fitScale + offsetY;
			if (sx >= cullLeft && sx <= cullRight && sy >= cullTop && sy <= cullBottom) {
				visibleProjected.push({ ...p, sx, sy });
			}
		}
		// Use pre-culled list for all rendering passes; fall back to full list if culling
		// eliminated everything (e.g., all nodes clustered off-screen during rapid pan)
		const renderList = visibleProjected.length > 0 ? visibleProjected : projectedWithZ.map(p => ({
			...p, sx: p.px * fitScale + offsetX, sy: p.py * fitScale + offsetY
		}));
		pt.end('render:projection');

		// === GLOBAL ATMOSPHERIC EFFECTS BY EVOLUTION STAGE ===
		pt.start('render:atmosphere');
		if (sceneLOD.showAtmosphere) {
			try {
				// ── Night dimming: reduce atmosphere intensity by ambient light ──
				const nightAlpha = this.network.ambientLight ?? 1.0;
				this.ctx.save();
				if (nightAlpha < 1.0) this.ctx.globalAlpha = 0.3 + nightAlpha * 0.7;
				this.renderGlobalAtmosphere();
				this.ctx.restore();
				// Reset canvas state after atmosphere rendering
				this.ctx.globalAlpha = 1.0;
				this.ctx.globalCompositeOperation = 'source-over';
			} catch (error) {
				console.error('[UI] renderGlobalAtmosphere failed:', error);
			}
		}
		pt.end('render:atmosphere');

		// COMPREHENSIVE ENERGY VISUALIZATION
		// Draw energy fields from organs first (background layer)
		pt.start('render:energyFields');
		if (this.network.energySystem && sceneLOD.showEnergyFields) {
			const organFields = this.network.energySystem.getOrganFields();
			for (const field of organFields) {
				this.renderEnergyField(field, fitScale, offsetX, offsetY);
			}
		}
		pt.end('render:energyFields');

		// OPTIMIZATION: Batch edge drawing with visual evolution + LOD
		// Draw edges in 3D (no z sorting needed for lines)
		pt.start('render:edges');
		this.ctx.save();
		
		// Apply LOD edge opacity to entire edge layer
		const effectiveEdgeAlpha = sceneLOD.edgeAlpha;
		
		// Build fast lookup of visible node IDs from pre-culled renderList
		const visibleNodeIds = new Set<number>();
		for (const p of renderList) visibleNodeIds.add(p.node.id);
		// Also build screen-position lookup for visible nodes to avoid re-projection
		const visibleScreenPos = new Map<number, { x: number; y: number }>();
		for (const p of renderList) visibleScreenPos.set(p.node.id, { x: p.sx, y: p.sy });

		// Helper: get interpolated 3D position for a node index (used for edge endpoints)
		const getInterpPos = (idx: number): { x: number; y: number; z: number } => {
			const ipos = hasInterpolation ? this.network.getInterpolatedPosition(idx) : null;
			if (ipos) return ipos;
			const n = this.network.nodes[idx];
			return n ? { x: n.x, y: n.y, z: n.z ?? 0 } : { x: 0, y: 0, z: 0 };
		};

		// Group strokes together to reduce state changes
		for (let edgeIdx = 0; edgeIdx < this.network.edges.length; edgeIdx++) {
			const edge = this.network.edges[edgeIdx];
			// ── Fast cull: skip edges where neither endpoint is visible ──
			if (!visibleNodeIds.has(edge.from) && !visibleNodeIds.has(edge.to)) continue;
			
			const from = this.network.nodes[edge.from];
			const to = this.network.nodes[edge.to];
			if (!from || !to) continue;
			
			// Use pre-computed screen positions when available, fall back to interpolated projection
			const fromPos = visibleScreenPos.get(edge.from);
			const toPos = visibleScreenPos.get(edge.to);
			const p1 = fromPos || (() => { const p = getInterpPos(edge.from); return this.project3D(p.x, p.y, p.z, this.rotation, fitScale, offsetX, offsetY); })();
			const p2 = toPos || (() => { const p = getInterpPos(edge.to); return this.project3D(p.x, p.y, p.z, this.rotation, fitScale, offsetX, offsetY); })();
			
			// LOD: Distance culling for connections
			if (!shouldRenderEdge(p1.x, p1.y, p2.x, p2.y, sceneLOD.maxEdgeDistance)) {
				continue;
			}
			
			// Also check visualEvolution's own culling
			const fromProps = this.getNodeVisualProperties(from);
			if (!this.visualEvolution.shouldRenderConnection(p1.x, p1.y, p2.x, p2.y, fromProps.stage)) {
				continue;
			}
			
			// Get visual properties
			const connectionState = this.visualEvolution.getConnectionVisualState(edge, from, to, fromProps);
			// Apply LOD edge opacity
			connectionState.opacity *= effectiveEdgeAlpha;
			
			// Calculate energy flow progress
			let progress = 0.5;
			if (
				this.network.edgeEnergyFlow &&
				typeof this.network.edgeEnergyFlow !== 'boolean' &&
				typeof (this.network.edgeEnergyFlow as { get?: Function }).get === 'function'
			) {
				progress = (this.network.edgeEnergyFlow as { get: (idx: number) => number }).get(edgeIdx) ?? 0.5;
			}
			
			// Render evolved connection
			this.visualEvolution.renderConnection(
				this.ctx,
				p1.x, p1.y,
				p2.x, p2.y,
				connectionState,
				progress
			);
		}
		
		this.ctx.restore();
		pt.end('render:edges');

		// Render energy flows with type-specific visuals
		pt.start('render:energyFlows');
		if (this.network.energySystem) {
			const energyFlows = this.network.energySystem.getEnergyFlows();
			for (const flow of energyFlows) {
				this.renderEnergyFlow(flow, fitScale, offsetX, offsetY);
			}
			
			// Render psychionic cascades
			const cascades = this.network.energySystem.getPsychionicCascades();
			for (const cascade of cascades) {
				this.renderPsychionicCascade(cascade, fitScale, offsetX, offsetY);
			}
		}
		pt.end('render:energyFlows');

		// --- Draw nodes in z-sorted order for proper overlap ---
		const minId = renderList.length > 0 ? Math.min(...renderList.map(p => p.node.id)) : 0;
		const maxId = renderList.length > 0 ? Math.max(...renderList.map(p => p.node.id)) : 1;

		// OPTIMIZATION: Batch node rendering with visual evolution
		pt.start('render:nodes');
		this.ctx.save();
		
		// First pass: Render glows (LOD-aware) — uses pre-culled renderList
		for (const p of renderList) {
			try {
				const node = p.node;
				const x = p.sx; // Pre-computed screen X
				const y = p.sy; // Pre-computed screen Y
				
				const visualProps = this.getNodeVisualProperties(node);
				const visualState = this.visualEvolution.getNodeVisualState(node, visualProps);
				
				// LOD: Selective glow rendering (use scene element LOD)
				if (!shouldRenderGlow(visualState.glowIntensity, sceneLOD.visualLODLevel)) {
					continue;
				}
				
				// Render glow
				this.visualEvolution.renderNodeGlow(this.ctx, x, y, visualState);
			} catch (error) {
				console.error('[UI] Node glow render failed:', error);
			}
		}
		// Reset state after glow pass
		this.ctx.globalAlpha = 1.0;
		
		// Second pass: Render node shapes — uses pre-culled renderList
		for (const p of renderList) {
			try {
				const node = p.node;
				const x = p.sx; // Pre-computed screen X
				const y = p.sy; // Pre-computed screen Y
				
				const visualProps = this.getNodeVisualProperties(node);
				const visualState = this.visualEvolution.getNodeVisualState(node, visualProps);
				
				// Render node shape (viewport already pre-culled)
				this.visualEvolution.renderNodeShape(this.ctx, x, y, visualState, visualProps);
				
				// LOD-aware particle emission
				if (shouldEmitParticle(sceneLOD.particleMultiplier) && 
				    Math.random() < visualState.particleEmissionRate * deltaTime) {
					this.visualEvolution.emitParticle(x, y, visualProps);
				}
			} catch (error) {
				console.error('[UI] Node shape render failed:', error);
			}
		}
		// Reset state after shape pass
		this.ctx.globalAlpha = 1.0;
		
		// Third pass: Render particles
		const particles = this.visualEvolution.getParticles();
		for (const particle of particles) {
			if (particle.opacity > 0) {
				this.ctx.save();
				this.ctx.globalAlpha = Math.min(1.0, Math.max(0, particle.opacity));
				this.ctx.fillStyle = particle.color;
				this.ctx.beginPath();
				this.ctx.arc(particle.x, particle.y, Math.max(0.1, particle.size), 0, Math.PI * 2);
				this.ctx.fill();
				this.ctx.restore();
			}
		}
		
		this.ctx.restore();
		pt.end('render:nodes');

		// Draw crystal heart node (special rendering)
		if (typeof this.network.crystalHeartId === 'number') {
			const heart = this.network.nodes[this.network.crystalHeartId];
			if (heart) {
				this.ctx.save();
				const bounds = this.getNetworkBounds();
				const netWidth = bounds.maxX - bounds.minX;
				const netHeight = bounds.maxY - bounds.minY;
				const scale = Math.min(1.5, 1 + this.network.nodes.length / 40);
				const margin = 40 * scale;
				const availableWidth = this.canvas.width - margin * 2;
				const availableHeight = this.canvas.height - margin * 2;
				const fitScale = Math.min(
					availableWidth / (netWidth || 1),
					availableHeight / (netHeight || 1),
					1.5
				);
				const offsetX = margin + (availableWidth / 2) - ((bounds.minX + bounds.maxX) / 2) * fitScale;
				const offsetY = margin + (availableHeight / 2) - ((bounds.minY + bounds.maxY) / 2) * fitScale;
				const x = heart.x * fitScale + offsetX;
				const y = heart.y * fitScale + offsetY;
				const radius = Math.max(0, 18 * fitScale + Math.sin(Date.now() / 200) * 2);
				this.ctx.beginPath();
				this.ctx.arc(x, y, radius, 0, 2 * Math.PI);
				this.ctx.fillStyle = '#e0b0ff';
				this.ctx.shadowColor = '#e0b0ff';
				this.ctx.shadowBlur = 16;
				this.ctx.globalAlpha = 0.8;
				this.ctx.fill();
				this.ctx.lineWidth = 2;
				this.ctx.strokeStyle = '#a03cff';
				this.ctx.stroke();
				this.ctx.restore();
			}
		}

		// --- Lightning Expansion Visualization (LOD-gated) ---
		if (sceneLOD.showLightning && typeof this.network.crystalHeartId === 'number') {
			const heart = this.network.nodes[this.network.crystalHeartId];
			if (heart) {
				const heartProj = this.project3D(heart.x, heart.y, heart.z ?? 0, this.rotation, fitScale, offsetX, offsetY);
				for (const node of this.network.nodes) {
					// Detect lightning nodes by color and highlight
					if (node.color === 'hsl(60, 100%, 80%)' && node.highlight && node.highlight > 80) {
						const nodeProj = this.project3D(node.x, node.y, node.z ?? 0, this.rotation, fitScale, offsetX, offsetY);
						this.ctx.save();
						this.ctx.strokeStyle = 'rgba(255,255,180,0.95)';
						this.ctx.lineWidth = 4.5;
						this.ctx.shadowColor = '#ffe066';
						this.ctx.shadowBlur = 24;
						this.ctx.globalAlpha = 0.85;
						this.ctx.beginPath();
						this.ctx.moveTo(heartProj.x, heartProj.y);

						// Jagged lightning effect: interpolate with random offsets
						const steps = 6;
						for (let s = 1; s < steps; s++) {
							const t = s / steps;
							const ix = heartProj.x + (nodeProj.x - heartProj.x) * t + (Math.random() - 0.5) * 18;
							const iy = heartProj.y + (nodeProj.y - heartProj.y) * t + (Math.random() - 0.5) * 18;
							this.ctx.lineTo(ix, iy);
						}
						this.ctx.lineTo(nodeProj.x, nodeProj.y);
						this.ctx.stroke();
						this.ctx.restore();

						// Optionally, draw a glowing node at the tip
						this.ctx.save();
						this.ctx.beginPath();
						this.ctx.arc(nodeProj.x, nodeProj.y, 14, 0, 2 * Math.PI);
						this.ctx.fillStyle = 'rgba(255,255,180,0.85)';
						this.ctx.shadowColor = '#ffe066';
						this.ctx.shadowBlur = 32;
						this.ctx.globalAlpha = 0.95;
						this.ctx.fill();
						this.ctx.restore();

					// (Zap sound handled by simulator's built-in zap system)
					}
				}
			}
		}
		
		// --- 3D Limb Rendering (LOD-gated) ---
		if (sceneLOD.showLimbs) {
			this.renderCreatureLimbs(fitScale, offsetX, offsetY);
		}

		// Psychic potential comparison UI (core purpose)
		// ── Gated: heavy O(nodes) loop + DOM only when stats panel updates ──
		if (sceneLOD.updateStatsPanel) {
		pt.start('render:psychic');
		type CompareNetworkResult = {
			score: number;
			compatibility: number;
			details: string;
		};
		const compareNetwork = (window as Window & { compareNetwork?: () => CompareNetworkResult }).compareNetwork;
		const psychicDiv = (this as any)._psychicDiv as HTMLDivElement;
		if (compareNetwork && psychicDiv) {
			const result = compareNetwork();
			const colorMatch = result.details.match(/ColorCategory=(\w+)/);
			const colorCategory = colorMatch ? colorMatch[1] : 'unknown';

			const zodiacMap: Record<string, string> = {
				red: 'Aries', orange: 'Leo', yellow: 'Sagittarius',
				green: 'Virgo', blue: 'Aquarius', cyan: 'Gemini',
				purple: 'Pisces', pink: 'Libra', brown: 'Taurus',
				gray: 'Capricorn', black: 'Scorpio', white: 'Cancer'
			};
			const colorStats: Record<string, number> = {
				red: 0, orange: 0, yellow: 0, green: 0, blue: 0, cyan: 0, purple: 0, pink: 0, brown: 0, gray: 0, black: 0, white: 0
			};
			for (const node of this.network.nodes) {
				const hueMatch = node.color.match(/hsl\((\d+)/);
				if (!hueMatch) continue;
				const hue = parseInt(hueMatch[1]);
				const colorType = hue < 20 || hue >= 340 ? 'red' : hue < 40 ? 'orange' : hue < 60 ? 'yellow' : hue < 80 ? 'brown' : hue < 100 ? 'gray' : hue < 120 ? 'black' : hue < 150 ? 'white' : hue < 200 ? 'cyan' : hue < 260 ? 'blue' : hue < 320 ? 'purple' : 'pink';
				if (colorStats.hasOwnProperty(colorType)) colorStats[colorType]++;
			}

			let colorStatsHtml = '';
			for (const key of Object.keys(colorStats)) {
				if (colorStats[key] === 0) continue; // Skip empty
				const zodiac = zodiacMap[key] || key;
				colorStatsHtml += `<span style="color:${key};font-weight:bold;margin-right:8px;">${key.charAt(0).toUpperCase() + key.slice(1)}:${colorStats[key]}</span>`;
			}

			// Use dedicated psychic panel — direct assignment, no += on massive DOM
			psychicDiv.style.display = 'block';
			psychicDiv.innerHTML = `
				<div style="font-weight:bold;margin-bottom:4px;">🔮 Psychic: ${result.score.toFixed(2)} | ${(result.compatibility * 100).toFixed(0)}% compat | <span style="color:${colorCategory};">${colorCategory}</span> (${zodiacMap[colorCategory] || '?'})</div>
				<div style="font-size:0.8em;opacity:0.8;">${colorStatsHtml || '<i>No nodes yet</i>'}</div>
			`;
		} else if (psychicDiv) {
			psychicDiv.style.display = 'none';
		}
		pt.end('render:psychic');
		} // end updateStatsPanel guard

		// Node evolution/growth buzz sound (cooldown-gated: max 1 per 8 frames)
		pt.start('render:sounds');
		if (this.network.nodes.length > this.lastNodeCount && this.frameCount % 8 === 0) {
			const color = this.network.nodes.length > 0 ? this.network.nodes[this.network.nodes.length - 1].color : 'hsl(220,70%,60%)';
			this.playBuzz(undefined, 0.32, 0.003, 0, color);
		}
		this.lastNodeCount = this.network.nodes.length;

		// Crystal heart hum (subtle, modulated by pulse)
		const heartPulseStep = Math.floor(this.network.step / this.network.crystalHeartPulseInterval);
		if (typeof this.network.crystalHeartId === 'number') {
			if (!this.heartHumOsc) {
				try {
					this.startHeartHum(); // fire-and-forget: no await needed
				} catch (err) {
					console.error('Error starting heart hum:', err);
				}
			}
			if (this.heartHumGain && (this as any).heartHumPanner) {
				const phase = Math.sin(Date.now() / 400);
				const base = 0.0006; // increased base level for audibility
				const pulse = (heartPulseStep % 2 === 0) ? 0.0006 : 0.0010; // stronger pulse depth
				const ctx = this.network.audioContext;
				if (ctx) {
					this.heartHumGain.gain.setValueAtTime(base + pulse * Math.abs(phase), ctx.currentTime);
					(this as any).heartHumPanner.pan.setValueAtTime(Math.sin(Date.now() / 1200) * 0.7, ctx.currentTime);
				}
			}
		} else {
			this.stopHeartHum();
		}
		this.lastHeartPulseStep = heartPulseStep;
		pt.end('render:sounds');

		// Show tooltip for node under mouse
		let hoveredNode: Node | null = null;
		if (this.mouse) {
			for (const node of this.network.nodes) {
				const proj = this.project3D(node.x, node.y, node.z ?? 0, this.rotation, fitScale, offsetX, offsetY);
				const dist = Math.hypot(this.mouse.x - proj.x, this.mouse.y - proj.y);
				if (dist < 16 * fitScale) {
					hoveredNode = { ...node, nodeType: node.nodeType as NodeType, vz: node.vz ?? 0 };
					break;
				}
			}
		}
		this.showNodeTooltip(hoveredNode);

		// Draw sun in 3D space if enabled
		if (this.network.sunEnabled && this.network.sunPosition) {
			const sun = this.network.sunPosition;
			const sunProj = this.project3D(sun.x, sun.y, sun.z, this.rotation, fitScale, offsetX, offsetY);
			this.ctx.save();
			this.ctx.beginPath();
			this.ctx.arc(sunProj.x, sunProj.y, 48, 0, 2 * Math.PI);
			const gradient = this.ctx.createRadialGradient(sunProj.x, sunProj.y, 0, sunProj.x, sunProj.y, 48);
			gradient.addColorStop(0, 'rgba(255,255,180,1)');
			gradient.addColorStop(0.5, 'rgba(255,220,80,0.7)');
			gradient.addColorStop(1, 'rgba(255,220,80,0.05)');
			this.ctx.fillStyle = gradient;
			this.ctx.shadowColor = '#ffe066';
			this.ctx.shadowBlur = 120;
			this.ctx.globalAlpha = 0.95;
			this.ctx.fill();
			this.ctx.restore();

			// Optionally, draw sun rays
			this.ctx.save();
			this.ctx.strokeStyle = 'rgba(255,255,80,0.18)';
			this.ctx.lineWidth = 4;
			for (let i = 0; i < 16; i++) {
				const angle = (Math.PI * 2 * i) / 16;
				const r1 = 48, r2 = 120;
				this.ctx.beginPath();
				this.ctx.moveTo(sunProj.x + Math.cos(angle) * r1, sunProj.y + Math.sin(angle) * r1);
				this.ctx.lineTo(sunProj.x + Math.cos(angle) * r2, sunProj.y + Math.sin(angle) * r2);
				this.ctx.stroke();
			}
			this.ctx.restore();
		}

		// --- Evolve sunlight haze sound (routes through ambience bus) ---
		if (this.network.sunEnabled) {
			if (!this._sunHazeOsc) {
				const ctx = this.network.audioContext;
				if (ctx) {
					const osc = ctx.createOscillator();
					const gain = ctx.createGain();
					osc.type = 'sine';
					osc.frequency.value = 18 + Math.random() * 6;
					gain.gain.value = 0.0001;
					osc.connect(gain);
					// Route through AudioEngine ambience bus
					try {
						const engine = getAudioEngine();
						if (engine.context === ctx) {
							gain.connect(engine.getBusGain('ambience'));
						} else {
							gain.connect(this.network.getAudioOutput());
						}
					} catch {
						gain.connect(this.network.getAudioOutput());
					}
					osc.start();
					this._sunHazeOsc = osc;
					this._sunHazeGain = gain;
				}
			}
			if (this._sunHazeGain) {
				const phase = Math.sin(Date.now() / 1200);
				this._sunHazeGain.gain.value = 0.01; // Reduced to prevent clipping
			}
		} else {
			if (this._sunHazeOsc) {
				this._sunHazeOsc.stop();
				this._sunHazeOsc.disconnect();
				this._sunHazeOsc = null;
			}
			if (this._sunHazeGain) {
				this._sunHazeGain.disconnect();
				this._sunHazeGain = null;
			}
		}

		// --- Magnify sphere for zooming over sub-nodal growths ---
		if (this.zoomCenter) {
			// Draw magnifying glass
			this.ctx.save();
			this.ctx.beginPath();
			this.ctx.arc(this.zoomCenter.x, this.zoomCenter.y, Math.max(1, this.zoomRadius), 0, 2 * Math.PI);
			this.ctx.clip();

			// Draw zoomed nodes inside sphere — uses pre-culled renderList with pre-computed positions
			for (const p of renderList) {
				const node = p.node;
				const x = p.sx; // Pre-computed screen X
				const y = p.sy; // Pre-computed screen Y
				const dist = Math.hypot(x - this.zoomCenter.x, y - this.zoomCenter.y);
				if (dist < this.zoomRadius) {
					// Magnify position relative to zoom center
					const zx = this.zoomCenter.x + (x - this.zoomCenter.x) * this.zoomScale;
					const zy = this.zoomCenter.y + (y - this.zoomCenter.y) * this.zoomScale;
					const minId = renderList.length > 0 ? Math.min(...renderList.map(p => p.node.id)) : 0;
					const maxId = renderList.length > 0 ? Math.max(...renderList.map(p => p.node.id)) : 1;
					const minRadius = 7.5, maxRadius = 16;
					const ageFactor = (maxId === minId) ? 1 : 1 - (node.id - minId) / (maxId - minId);
					const radius = Math.max(0.1, (minRadius + (maxRadius - minRadius) * ageFactor) * this.zoomScale * 1.15);
					this.ctx.save();
					this.ctx.beginPath();
					this.ctx.arc(zx, zy, radius, 0, 2 * Math.PI);
					this.ctx.fillStyle = node.color;
					this.ctx.shadowColor = node.color;
					this.ctx.shadowBlur = 12;
					this.ctx.globalAlpha = 0.92;
					this.ctx.fill();
					this.ctx.lineWidth = 2.2;
					this.ctx.strokeStyle = '#222';
					this.ctx.globalAlpha = 1;
					this.ctx.stroke();
					this.ctx.restore();

					// Draw node id for clarity
					this.ctx.save();
					this.ctx.font = `${Math.max(12, radius * 0.8)}px Arial`;
					this.ctx.fillStyle = '#fff';
					this.ctx.textAlign = 'center';
					this.ctx.textBaseline = 'middle';
					this.ctx.globalAlpha = 0.85;
					this.ctx.fillText(`${node.id}`, zx, zy);
					this.ctx.restore();
				}
			}

			// Draw magnifier border
			this.ctx.restore();
			this.ctx.save();
			this.ctx.beginPath();
			this.ctx.arc(this.zoomCenter.x, this.zoomCenter.y, Math.max(1, this.zoomRadius), 0, 2 * Math.PI);
			this.ctx.lineWidth = 4;
			this.ctx.strokeStyle = '#e0b0ff';
			this.ctx.shadowColor = '#a03cff';
			this.ctx.shadowBlur = 18;
			this.ctx.globalAlpha = 0.7;
			this.ctx.stroke();
			this.ctx.restore();
		}
		// --- Auto expand all structures to the middle point, balanced space ---
		// Calculate center of all nodes
		const nodesToBalance: Node[] = typeof this.network.getRenderableNodes === 'function'
			? this.network.getRenderableNodes()
			: this.network.nodes;
		if (nodesToBalance.length > 0) {
			const avgX = nodesToBalance.reduce((sum: number, n: Node) => sum + n.x, 0) / nodesToBalance.length;
			const avgY = nodesToBalance.reduce((sum: number, n: Node) => sum + n.y, 0) / nodesToBalance.length;
			const avgZ = nodesToBalance.reduce((sum: number, n: Node) => sum + (n.z ?? 0), 0) / nodesToBalance.length;
			const targetX = this.network.spaceSize.width / 2;
			const targetY = this.network.spaceSize.height / 2;
			const targetZ = (this.network.spaceSize.depth ?? 800) / 2;
			const spread = Math.max(1, Math.min(120, 0.2 * Math.max(this.network.spaceSize.width, this.network.spaceSize.height)));
			for (const node of nodesToBalance) {
				// Move each node towards balanced center, but keep some spread
				node.x += (targetX - avgX) * 0.08 + (Math.random() - 0.5) * 0.01 * spread;
				node.y += (targetY - avgY) * 0.08 + (Math.random() - 0.5) * 0.01 * spread;
				if (typeof node.z === 'number') {
					node.z += (targetZ - avgZ) * 0.08 + (Math.random() - 0.5) * 0.01 * spread;
				}
			}
		}
		// ── Non-render updates (throttled: every 20 frames = ~1.3s at 15fps) ──
		if (this.frameCount % 20 === 0) {
			this.updateBreathingDisplays();
			if (this.modernUI && this.useModernUI) {
				try { this.modernUI.update(); } catch { /* non-critical */ }
			}
			this.updateEquilibriumLayout();
		}
	}

	restartAllSounds() {
		// Stop heart hum
		this.stopHeartHum();
		// Stop sun haze
		if (this._sunHazeOsc) {
			try { this._sunHazeOsc.stop(); } catch {}
			try { this._sunHazeOsc.disconnect(); } catch {}
			this._sunHazeOsc = null;
		}
		if (this._sunHazeGain) {
			try { this._sunHazeGain.disconnect(); } catch {}
			this._sunHazeGain = null;
		}
		// Optionally, reset sound statistics
		this.buzzCountTotal = 0;
		this.sparkleCountTotal = 0;
		this.heartHumStartCount = 0;
		// Assign restart button handler inside constructor to ensure 'this.restartBtn' is defined
		this.restartBtn.onclick = () => {
			if (typeof this.restartAllSounds === 'function') {
				this.restartAllSounds();
			}
			window.location.reload();
		};
	}

	// Update equilibrium layout in real-time
	updateEquilibriumLayout() {
		if (!this.equilibriumCanvas || !this.equilibriumCtx) {
			return;
		}
		
		// Check if panel is still visible
		const panel = document.getElementById('equilibrium-layout-panel');
		if (!panel) {
			// Panel was closed, clear references
			this.equilibriumCanvas = null;
			this.equilibriumCtx = null;
			this.equilibriumWidth = 0;
			this.equilibriumHeight = 0;
			return;
		}
		
		const ctx = this.equilibriumCtx;
		// Ensure canvas matches panel size and device pixel ratio
		const dpr = window.devicePixelRatio || 1;
		const clientW = Math.max(1, panel.clientWidth);
		const clientH = Math.max(1, panel.clientHeight);
		if (this.equilibriumWidth !== clientW || this.equilibriumHeight !== clientH || this.equilibriumCanvas.width !== Math.floor(clientW * dpr) || this.equilibriumCanvas.height !== Math.floor(clientH * dpr)) {
			this.equilibriumWidth = clientW;
			this.equilibriumHeight = clientH;
			this.equilibriumCanvas.width = Math.floor(clientW * dpr);
			this.equilibriumCanvas.height = Math.floor(clientH * dpr);
			this.equilibriumCanvas.style.width = `${clientW}px`;
			this.equilibriumCanvas.style.height = `${clientH}px`;
		}
		const width = this.equilibriumWidth;
		const height = this.equilibriumHeight;
		
		// Get current network state
		const nodes = this.network.nodes.map(n => ({ ...n })) as Array<Node & { _eqX?: number; _eqY?: number }>;
		const edges = this.network.edges.map(e => ({ ...e }));
		
		// Calculate node positions (reuse existing positions if they exist, otherwise recalculate)
		if (nodes.length === 0) return;
		
		const gridCols = Math.ceil(Math.sqrt(nodes.length));
		const gridRows = Math.ceil(nodes.length / gridCols);
		const inset = 16;
		const cellW = Math.max(1, (width - inset * 2) / gridCols);
		const cellH = Math.max(1, (height - inset * 2) / gridRows);
		
		// Assign positions to nodes that don't have them, or update based on node count changes
		nodes.forEach((n, i) => {
			if (typeof n._eqX === 'undefined' || typeof n._eqY === 'undefined') {
				const col = i % gridCols;
				const row = Math.floor(i / gridCols);
				n._eqX = col * cellW + cellW / 2;
				n._eqY = row * cellH + cellH / 2;
			}
		});
		
		// Simple repulsion for better visualization (fewer iterations for performance)
		for (let iter = 0; iter < 3; iter++) {
			for (let i = 0; i < nodes.length; i++) {
				for (let j = i + 1; j < nodes.length; j++) {
					const a = nodes[i], b = nodes[j];
					if (typeof a._eqX === 'undefined' || typeof a._eqY === 'undefined' || 
						typeof b._eqX === 'undefined' || typeof b._eqY === 'undefined') continue;
					
					const dx = b._eqX - a._eqX, dy = b._eqY - a._eqY;
					const dist = Math.hypot(dx, dy);
					const minDist = 32;
					if (dist < minDist) {
						const force = (minDist - dist) * 0.12;
						const fx = force * dx / (dist || 1);
						const fy = force * dy / (dist || 1);
						a._eqX -= fx;
						a._eqY -= fy;
						b._eqX += fx;
						b._eqY += fy;
					}
				}
			}
		}
		
		// Redraw canvas
		ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		ctx.clearRect(0, 0, width, height);
		
		// Draw edges first
		edges.forEach(e => {
			const from = nodes.find(n => n.id === e.from);
			const to = nodes.find(n => n.id === e.to);
			if (!from || !to || typeof from._eqX === 'undefined' || typeof from._eqY === 'undefined' ||
				typeof to._eqX === 'undefined' || typeof to._eqY === 'undefined') return;
			
			// Use same color logic as main simulation
			const edgeHue = (parseInt(from.color.match(/hsl\((\d+)/)?.[1] ?? '0') + parseInt(to.color.match(/hsl\((\d+)/)?.[1] ?? '0')) / 2;
			
			ctx.save();
			ctx.globalAlpha = 0.7;
			ctx.beginPath();
			ctx.moveTo((from._eqX ?? 0) + inset, (from._eqY ?? 0) + inset);
			ctx.lineTo((to._eqX ?? 0) + inset, (to._eqY ?? 0) + inset);
			ctx.strokeStyle = `hsla(${edgeHue},90%,65%,0.85)`;
			ctx.lineWidth = 1.8;
			ctx.shadowColor = `hsla(${edgeHue},90%,80%,0.35)`;
			ctx.shadowBlur = 4;
			ctx.stroke();
			ctx.restore();
		});
		
		// Draw nodes
		nodes.forEach(n => {
			if (typeof n._eqX === 'undefined' || typeof n._eqY === 'undefined') return;
			
			ctx.save();
			ctx.beginPath();
			ctx.arc((n._eqX ?? 0) + inset, (n._eqY ?? 0) + inset, 8, 0, 2 * Math.PI);
			ctx.fillStyle = n.color;
			ctx.shadowColor = n.color;
			ctx.shadowBlur = 4;
			ctx.globalAlpha = 0.92;
			ctx.fill();
			ctx.lineWidth = 1.4;
			ctx.strokeStyle = '#222';
			ctx.globalAlpha = 1;
			ctx.stroke();
			// Draw node id
			ctx.font = 'bold 11px Arial';
			ctx.fillStyle = '#222';
			ctx.textAlign = 'center';
			ctx.textBaseline = 'middle';
			ctx.globalAlpha = 0.8;
			ctx.fillText(`${n.id}`, (n._eqX ?? 0) + inset, (n._eqY ?? 0) + inset);
			ctx.restore();
		});
	}

	// Equilibrium/Wimmelbilderbuch layout visualization (bottom bar)
	showEquilibriumLayout() {
		const panelId = 'equilibrium-layout-panel';
		const barHeight = 160; // px height for bottom bar
		const width = window.innerWidth;
		const height = barHeight;

		// --- Disassemble: extract nodes/edges ---
		const nodes = this.network.nodes.map(n => ({ ...n })) as Array<Node & { _eqX: number; _eqY: number }>;
		const edges = this.network.edges.map(e => ({ ...e }));

		// --- Recognize: assign grid positions for maximal clarity/density ---
		const gridCols = Math.ceil(Math.sqrt(nodes.length));
		const gridRows = Math.ceil(nodes.length / gridCols);
		const layoutInset = 16;
		const cellW = Math.max(1, (width - layoutInset * 2) / gridCols);
		const cellH = Math.max(1, (height - layoutInset * 2) / gridRows);
		nodes.forEach((n, i) => {
			const col = i % gridCols;
			const row = Math.floor(i / gridCols);
			n._eqX = col * cellW + cellW / 2;
			n._eqY = row * cellH + cellH / 2;
		});

		// --- Optimize: spread nodes to avoid overlap, keep edges visible ---
		for (let iter = 0; iter < 12; iter++) {
			for (let i = 0; i < nodes.length; i++) {
				for (let j = i + 1; j < nodes.length; j++) {
					const a = nodes[i], b = nodes[j];
					const dx = b._eqX - a._eqX, dy = b._eqY - a._eqY;
					const dist = Math.hypot(dx, dy);
					const minDist = 32;
					if (dist < minDist) {
						const force = (minDist - dist) * 0.12;
						const fx = force * dx / (dist || 1);
						const fy = force * dy / (dist || 1);
						a._eqX -= fx;
						a._eqY -= fy;
						b._eqX += fx;
						b._eqY += fy;
					}
				}
			}
		}

		// --- Reassemble: draw everything clearly ---
		const canvas = document.createElement('canvas');
		// HiDPI-safe sizing
		const dpr = window.devicePixelRatio || 1;
		canvas.width = Math.max(1, Math.floor(width * dpr));
		canvas.height = Math.max(1, Math.floor(height * dpr));
		canvas.style.width = '100%';
		canvas.style.height = '100%';
		canvas.style.background = '#0f1116';
		canvas.style.display = 'block';
		canvas.style.zIndex = '10003';
		
		// Store references for real-time updates
		this.equilibriumCanvas = canvas;
		const ctx = canvas.getContext('2d');
		this.equilibriumCtx = ctx;
		this.equilibriumWidth = width;
		this.equilibriumHeight = height;
		
		if (ctx) {
			// Normalize to CSS pixels
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
			ctx.clearRect(0, 0, width, height);
			// Provide a small inset so nodes and shadows are fully visible
			const inset = 16; // px margin on each side
			const effW = Math.max(1, width - inset * 2);
			const effH = Math.max(1, height - inset * 2);
			// Draw edges first
			edges.forEach(e => {
				const from = nodes.find(n => n.id === e.from);
				const to = nodes.find(n => n.id === e.to);
				if (!from || !to) return;
				
				// Use same color logic as main simulation
				const edgeHue = (parseInt(from.color.match(/hsl\((\d+)/)?.[1] ?? '0') + parseInt(to.color.match(/hsl\((\d+)/)?.[1] ?? '0')) / 2;
				
				ctx.save();
				ctx.globalAlpha = 0.7;
				ctx.beginPath();
				ctx.moveTo(from._eqX + inset, from._eqY + inset);
				ctx.lineTo(to._eqX + inset, to._eqY + inset);
				ctx.strokeStyle = `hsla(${edgeHue},90%,65%,0.85)`;
				ctx.lineWidth = 1.8;
				ctx.shadowColor = `hsla(${edgeHue},90%,80%,0.35)`;
				ctx.shadowBlur = 4;
				ctx.stroke();
				ctx.restore();
			});
			// Draw nodes
			nodes.forEach(n => {
				ctx.save();
			ctx.beginPath();
			ctx.arc(n._eqX + inset, n._eqY + inset, 8, 0, 2 * Math.PI);
				ctx.fillStyle = n.color;
				ctx.shadowColor = n.color;
			ctx.shadowBlur = 4;
				ctx.globalAlpha = 0.92;
				ctx.fill();
			ctx.lineWidth = 1.4;
				ctx.strokeStyle = '#222';
				ctx.globalAlpha = 1;
				ctx.stroke();
				// Draw node id
			ctx.font = 'bold 11px Arial';
				ctx.fillStyle = '#222';
				ctx.textAlign = 'center';
				ctx.textBaseline = 'middle';
				ctx.globalAlpha = 0.8;
			ctx.fillText(`${n.id}`, n._eqX + inset, n._eqY + inset);
				ctx.restore();
			});
		}

		// --- Integration: bottom bar panel (no overlay) ---
		let panel = document.getElementById(panelId) as HTMLDivElement;
		if (!panel) {
			panel = document.createElement('div');
			panel.id = panelId;
			panel.style.position = 'fixed';
			panel.style.left = '0';
			panel.style.right = '0';
			panel.style.bottom = '0';
            panel.style.height = `${barHeight}px`;
            // Make the bottom panel non-intrusive so it doesn't read as a blurry overlay
            panel.style.background = 'rgba(20, 24, 33, 0.28)';
            panel.style.borderTop = '1px solid rgba(160,60,255,0.18)';
            panel.style.boxShadow = 'none';
			panel.style.padding = '0';
			panel.style.boxSizing = 'border-box';
			panel.style.zIndex = '10001';
			panel.style.display = 'block';
			panel.style.pointerEvents = 'auto';
			document.body.appendChild(panel);
		}
		panel.innerHTML = '';
		panel.appendChild(canvas);
	}

	// Render creature limbs with 3D projection
	renderCreatureLimbs(fitScale: number, offsetX: number, offsetY: number): void {
		if (!this.ctx) return;
		if (!this.network.limbSystem) return;
		
		const limbs = this.network.limbSystem.getLimbs();
		if (limbs.length === 0) return;
		
		this.ctx.save();
		
		for (const limb of limbs) {
			this.renderLimb(limb, fitScale, offsetX, offsetY);
		}
		
		this.ctx.restore();
	}
	
	// Render individual limb with 3D projection
	renderLimb(limb: any, fitScale: number, offsetX: number, offsetY: number): void {
		if (!this.ctx) return;
		if (limb.segments.length === 0) return;
		
		this.ctx.save();
		
		// Set limb-specific rendering properties
		const limbColor = this.getLimbColor(limb);
		const limbAlpha = this.getLimbAlpha(limb);
		const limbGlow = this.getLimbGlow(limb);
		
		this.ctx.globalAlpha = limbAlpha;
		this.ctx.strokeStyle = limbColor;
		this.ctx.fillStyle = limbColor;
		this.ctx.lineWidth = this.getLimbLineWidth(limb);
		
		// Add glow effect for active limbs
		if (limbGlow > 0) {
			this.ctx.shadowColor = limbColor;
			this.ctx.shadowBlur = limbGlow;
		}
		
		// Render limb segments with 3D projection
		for (let i = 0; i < limb.segments.length; i++) {
			const segment = limb.segments[i];
			this.renderLimbSegment(segment, limb, i, fitScale, offsetX, offsetY);
		}
		
		this.ctx.restore();
	}
	
	// Render individual limb segment with 3D projection
	renderLimbSegment(segment: any, limb: any, index: number, fitScale: number, offsetX: number, offsetY: number): void {
		if (!this.ctx) return;
		const time = Date.now() / 1000;
		const breathing = Math.sin(time * 2 + segment.id * 0.1) * 0.1;
		const energyPulse = Math.sin(time * 4 + segment.id * 0.2) * 0.2;
		
		// Project 3D coordinates to 2D
		const segmentProj = this.project3D(segment.x, segment.y, segment.z, this.rotation, fitScale, offsetX, offsetY);
		
		// Apply breathing and energy effects
		const x = segmentProj.x + breathing * 1.5;
		const y = segmentProj.y + energyPulse * 3;
		
		// Render segment based on limb type
		if (limb.type === 'fractal_branch' || limb.type === 'neural_extension') {
			// Render as connected line segments
			if (index > 0) {
				const prevSegment = limb.segments[index - 1];
				const prevProj = this.project3D(prevSegment.x, prevSegment.y, prevSegment.z, this.rotation, fitScale, offsetX, offsetY);
				this.ctx.beginPath();
				this.ctx.moveTo(prevProj.x, prevProj.y);
				this.ctx.lineTo(x, y);
				this.ctx.stroke();
			}
		} else if (limb.type === 'psychic_manifest' || limb.type === 'mystical_tendril') {
			// Render as glowing orbs
			this.ctx.beginPath();
			this.ctx.arc(x, y, Math.max(0.1, (segment.thickness ?? 1) + energyPulse * 2), 0, Math.PI * 2);
			this.ctx.fill();
		} else if (limb.type === 'wave_resonance' || limb.type === 'energy_conduit') {
			// Render as wavy lines
			this.renderWavySegment(segment, x, y, time, fitScale, offsetX, offsetY);
		} else {
			// Default rendering as simple lines
			if (index > 0) {
				const prevSegment = limb.segments[index - 1];
				const prevProj = this.project3D(prevSegment.x, prevSegment.y, prevSegment.z, this.rotation, fitScale, offsetX, offsetY);
				this.ctx.beginPath();
				this.ctx.moveTo(prevProj.x, prevProj.y);
				this.ctx.lineTo(x, y);
				this.ctx.stroke();
			}
		}
		
		// Add segment-specific effects
		if (segment.energy > 70) {
			this.renderSegmentEnergyEffect(segment, x, y);
		}
	}
	
	// Render wavy segment for wave-based limbs
	renderWavySegment(segment: any, x: number, y: number, time: number, fitScale: number, offsetX: number, offsetY: number): void {
		if (!this.ctx) return;
		const waveCount = 3;
		const waveAmplitude = 5;
		const waveLength = segment.length / waveCount;
		
		this.ctx.beginPath();
		
		// Start from the root node position
		const rootNode = this.network.nodes.find((n: any) => n.id === segment.parentSegment);
		if (rootNode) {
			const rootProj = this.project3D(rootNode.x, rootNode.y, rootNode.z, this.rotation, fitScale, offsetX, offsetY);
			this.ctx.moveTo(rootProj.x, rootProj.y);
		}
		
		// Create wave pattern
		for (let i = 1; i <= waveCount; i++) {
			const t = i / waveCount;
			const waveX = x * t;
			const waveY = y * t + Math.sin(time * 2 + t * Math.PI * 2) * waveAmplitude;
			this.ctx.lineTo(waveX, waveY);
		}
		
		this.ctx.stroke();
	}
	
	// Render energy effects for active segments
	renderSegmentEnergyEffect(segment: any, x: number, y: number): void {
		if (!this.ctx) return;
		const time = Date.now() / 1000;
		const pulse = Math.sin(time * 4 + segment.id * 0.1) * 0.5 + 0.5;
		
		this.ctx.save();
		this.ctx.globalAlpha = pulse * 0.6;
		this.ctx.beginPath();
		this.ctx.arc(x, y, 4 + pulse * 2, 0, Math.PI * 2);
		this.ctx.fill();
		this.ctx.restore();
	}
	
	// Get limb color based on type and state
	getLimbColor(limb: any): string {
		const baseColors: Record<string, string> = {
			'fractal_branch': '#ff6b6b',
			'psychic_manifest': '#9b59b6',
			'linguistic_appendage': '#3498db',
			'wave_resonance': '#1abc9c',
			'energy_conduit': '#f39c12',
			'sensing_probe': '#e74c3c',
			'vocal_modifier': '#2ecc71',
			'neural_extension': '#8e44ad',
			'defensive_spike': '#c0392b',
			'mystical_tendril': '#d35400',
			'temporal_phase': '#7f8c8d',
			'adaptive_structure': '#27ae60'
		};
		
		let color = baseColors[limb.type] || '#ffffff';
		
		// Modify color based on limb state
		if (limb.state === 'active') {
			color = this.brightenColor(color, 0.3);
		} else if (limb.state === 'dormant') {
			color = this.darkenColor(color, 0.5);
		} else if (limb.state === 'growing') {
			color = this.saturateColor(color, 0.2);
		}
		
		return color;
	}
	
	// Get limb alpha based on state and energy
	getLimbAlpha(limb: any): number {
		let alpha = 0.7;
		
		if (limb.state === 'active') {
			alpha = 0.9;
		} else if (limb.state === 'dormant') {
			alpha = 0.3;
		} else if (limb.state === 'growing') {
			alpha = 0.8;
		} else if (limb.state === 'atrophying') {
			alpha = 0.2;
		}
		
		// Adjust based on energy level
		const energyFactor = limb.currentEnergy / limb.energyCapacity;
		alpha *= 0.5 + energyFactor * 0.5;
		
		return Math.max(0.1, Math.min(1, alpha));
	}
	
	// Get limb glow intensity
	getLimbGlow(limb: any): number {
		if (limb.state !== 'active') return 0;
		
		const energyFactor = limb.currentEnergy / limb.energyCapacity;
		return energyFactor * 15;
	}
	
	// ENERGY SYSTEM RENDERING
	
	renderEnergyField(field: any, fitScale: number, offsetX: number, offsetY: number): void {
		if (!this.ctx) return;
		
		const proj = this.project3D(field.center.x, field.center.y, field.center.z, this.rotation, fitScale, offsetX, offsetY);
		
		// Render circular energy field with pulsing effect
		const radius = Math.max(0.1, (field.radius ?? 10) * fitScale);
		const alpha = 0.3 * (field.intensity ?? 0.5);
		
		this.ctx.save();
		this.ctx.globalAlpha = alpha;
		
		// Create gradient from center
		const gradient = this.ctx.createRadialGradient(proj.x, proj.y, 0, proj.x, proj.y, radius);
		gradient.addColorStop(0, `hsla(${field.phase * 57 % 360}, 100%, 70%, ${alpha})`);
		gradient.addColorStop(0.5, `hsla(${field.phase * 57 % 360}, 80%, 60%, ${alpha * 0.5})`);
		gradient.addColorStop(1, `hsla(${field.phase * 57 % 360}, 60%, 50%, 0)`);
		
		this.ctx.fillStyle = gradient;
		this.ctx.beginPath();
		this.ctx.arc(proj.x, proj.y, radius, 0, Math.PI * 2);
		this.ctx.fill();
		
		this.ctx.restore();
	}
	
	renderEnergyFlow(flow: any, fitScale: number, offsetX: number, offsetY: number): void {
		if (!this.ctx) return;
		
		const fromNode = this.network.nodes[flow.from];
		const toNode = this.network.nodes[flow.to];
		if (!fromNode || !toNode) return;
		
		const p1 = this.project3D(fromNode.x, fromNode.y, fromNode.z ?? 0, this.rotation, fitScale, offsetX, offsetY);
		const p2 = this.project3D(toNode.x, toNode.y, toNode.z ?? 0, this.rotation, fitScale, offsetX, offsetY);
		
		// Get color based on energy type
		const color = this.getEnergyTypeColor(flow.energyType);
		
		// Calculate position along edge
		const pulseX = p1.x + (p2.x - p1.x) * flow.progress;
		const pulseY = p1.y + (p2.y - p1.y) * flow.progress;
		
		// Render energy pulse
		this.ctx.save();
		this.ctx.globalAlpha = flow.intensity;
		this.ctx.fillStyle = color;
		this.ctx.shadowColor = color;
		this.ctx.shadowBlur = 8 * flow.intensity;
		
		const size = Math.max(0.1, 4 + (flow.intensity ?? 0) * 8);
		this.ctx.beginPath();
		this.ctx.arc(pulseX, pulseY, size, 0, Math.PI * 2);
		this.ctx.fill();
		
		this.ctx.restore();
	}
	
	renderPsychionicCascade(cascade: any, fitScale: number, offsetX: number, offsetY: number): void {
		if (!this.ctx) return;
		
		const originNode = this.network.nodes[cascade.origin];
		if (!originNode) return;
		
		const proj = this.project3D(originNode.x, originNode.y, originNode.z ?? 0, this.rotation, fitScale, offsetX, offsetY);
		
		// Render expanding psychic energy wave
		const age = Math.max(0, (Date.now() - cascade.time) / 2000);
		const radius = Math.max(0.1, (cascade.radius ?? 10) * fitScale * age * 2);
		const intensity = (cascade.intensity ?? 0.5) * (1 - age);
		
		if (intensity > 0) {
			this.ctx.save();
			this.ctx.globalAlpha = intensity * 0.6;
			this.ctx.strokeStyle = `hsl(${(280 + age * 80) % 360}, 100%, 80%)`;
			this.ctx.lineWidth = 3 + age * 5;
			this.ctx.shadowColor = `hsl(${(280 + age * 80) % 360}, 100%, 80%)`;
			this.ctx.shadowBlur = 20;
			this.ctx.beginPath();
			this.ctx.arc(proj.x, proj.y, radius, 0, Math.PI * 2);
			this.ctx.stroke();
			this.ctx.restore();
		}
	}
	
	getEnergyTypeColor(energyType: string): string {
		switch (energyType) {
			case 'neural':
				return 'hsla(200, 100%, 70%, 0.8)'; // Blue
			case 'metabolic':
				return 'hsla(120, 100%, 60%, 0.8)'; // Green
			case 'psychionic':
				return 'hsla(280, 100%, 70%, 0.8)'; // Purple
			case 'organ':
				return 'hsla(0, 100%, 60%, 0.8)'; // Red
			case 'breathing':
				return 'hsla(200, 60%, 70%, 0.8)'; // Cyan
			case 'vibrational':
				return 'hsla(60, 100%, 65%, 0.8)'; // Yellow
			case 'creative':
				return 'hsla(320, 100%, 70%, 0.8)'; // Magenta
			case 'emotional':
				return 'hsla(10, 100%, 65%, 0.8)'; // Orange-red
			case 'transcendent':
				return 'hsla(300, 100%, 80%, 0.9)'; // Pink
			default:
				return 'hsla(0, 0%, 70%, 0.6)'; // Gray
		}
	}
	
	// Get limb line width
	getLimbLineWidth(limb: any): number {
		const baseWidth = 2;
		const energyFactor = limb.currentEnergy / limb.energyCapacity;
		return baseWidth + energyFactor * 2;
	}
	
	// Color utility functions
	brightenColor(color: string, factor: number): string {
		const m = /hsl\(([-\d.]+),\s*([-\d.]+)%,\s*([-\d.]+)%\)/.exec(color);
		if (!m) return color;
		const h = parseFloat(m[1]);
		const s = parseFloat(m[2]);
		const l = Math.min(95, parseFloat(m[3]) + factor * 40);
		return `hsl(${h}, ${s}%, ${l}%)`;
	}

	darkenColor(color: string, factor: number): string {
		const m = /hsl\(([-\d.]+),\s*([-\d.]+)%,\s*([-\d.]+)%\)/.exec(color);
		if (!m) return color;
		const h = parseFloat(m[1]);
		const s = parseFloat(m[2]);
		const l = Math.max(5, parseFloat(m[3]) - factor * 40);
		return `hsl(${h}, ${s}%, ${l}%)`;
	}

	saturateColor(color: string, factor: number): string {
		const m = /hsl\(([-\d.]+),\s*([-\d.]+)%,\s*([-\d.]+)%\)/.exec(color);
		if (!m) return color;
		const h = parseFloat(m[1]);
		const s = Math.min(100, parseFloat(m[2]) + factor * 40);
		const l = parseFloat(m[3]);
		return `hsl(${h}, ${s}%, ${l}%)`;
	}
	
	// Render global atmospheric effects based on evolution stage
	private renderGlobalAtmosphere(): void {
		if (!this.ctx) return;
		
		const stage = this.getNetworkEvolutionStage();
		const width = this.canvas.width;
		const height = this.canvas.height;
		
		this.ctx.save();
		
		// Create atmospheric gradient based on stage
		const gradient = this.ctx.createRadialGradient(
			width / 2, height / 2, 0,
			width / 2, height / 2, Math.max(width, height) / 1.5
		);
		
		// Configure gradient colors based on evolution stage
		switch (stage) {
			case 'embryonic':
				// Dark, womb-like
				gradient.addColorStop(0, 'rgba(40, 30, 60, 0.15)');
				gradient.addColorStop(0.5, 'rgba(20, 15, 40, 0.08)');
				gradient.addColorStop(1, 'rgba(10, 5, 20, 0.02)');
				break;
			
			case 'primitive':
				// Dawn-like, warm
				gradient.addColorStop(0, 'rgba(80, 50, 40, 0.12)');
				gradient.addColorStop(0.5, 'rgba(60, 40, 50, 0.06)');
				gradient.addColorStop(1, 'rgba(30, 20, 30, 0.02)');
				break;
			
			case 'specialized':
				// Clear, organized
				gradient.addColorStop(0, 'rgba(60, 80, 100, 0.10)');
				gradient.addColorStop(0.5, 'rgba(40, 60, 80, 0.05)');
				gradient.addColorStop(1, 'rgba(20, 30, 40, 0.02)');
				break;
			
			case 'integrated':
				// Harmonious, balanced
				gradient.addColorStop(0, 'rgba(80, 100, 120, 0.12)');
				gradient.addColorStop(0.5, 'rgba(60, 80, 100, 0.06)');
				gradient.addColorStop(1, 'rgba(40, 60, 80, 0.03)');
				break;
			
			case 'transcendent':
				// Luminous, ethereal
				gradient.addColorStop(0, 'rgba(150, 130, 200, 0.15)');
				gradient.addColorStop(0.5, 'rgba(100, 80, 150, 0.08)');
				gradient.addColorStop(1, 'rgba(60, 40, 100, 0.03)');
				break;
			
			case 'mystical':
				// Magical, impossible colors (reduced brightness)
				const mysticalHue = (Date.now() / 50) % 360;
				gradient.addColorStop(0, `hsla(${mysticalHue}, 70%, 40%, 0.12)`);
				gradient.addColorStop(0.5, `hsla(${(mysticalHue + 60) % 360}, 60%, 35%, 0.08)`);
				gradient.addColorStop(1, `hsla(${(mysticalHue + 120) % 360}, 50%, 30%, 0.03)`);
				break;
			
			case 'divine':
				// Celestial, golden (reduced brightness)
				gradient.addColorStop(0, 'rgba(255, 230, 150, 0.15)');
				gradient.addColorStop(0.5, 'rgba(220, 190, 100, 0.10)');
				gradient.addColorStop(1, 'rgba(160, 140, 70, 0.04)');
				break;
			
			case 'cosmic':
				// Deep space, stellar
				gradient.addColorStop(0, 'rgba(40, 20, 80, 0.25)');
				gradient.addColorStop(0.3, 'rgba(60, 40, 120, 0.15)');
				gradient.addColorStop(0.7, 'rgba(20, 10, 40, 0.08)');
				gradient.addColorStop(1, 'rgba(5, 2, 10, 0.03)');
				break;
			
			case 'infinite':
				// All atmospheres simultaneously (reduced brightness to prevent washing out)
				const infiniteTime = Date.now() / 100;
				const layer1Hue = infiniteTime % 360;
				const layer2Hue = (infiniteTime * 1.618) % 360;
				const layer3Hue = (infiniteTime * 2.718) % 360;
				gradient.addColorStop(0, `hsla(${layer1Hue}, 80%, 45%, 0.15)`);
				gradient.addColorStop(0.33, `hsla(${layer2Hue}, 70%, 40%, 0.12)`);
				gradient.addColorStop(0.66, `hsla(${layer3Hue}, 60%, 35%, 0.08)`);
				gradient.addColorStop(1, 'rgba(100, 100, 120, 0.03)');
				break;
		}
		
		// Apply gradient
		this.ctx.fillStyle = gradient;
		this.ctx.fillRect(0, 0, width, height);
		
		// Add ambient particles for advanced stages
		if (['transcendent', 'mystical', 'divine', 'cosmic', 'infinite'].includes(stage)) {
			this.renderAmbientParticles(stage);
		}
		
		this.ctx.restore();
	}
	
	// Render ambient atmospheric particles
	private renderAmbientParticles(stage: EvolutionStage): void {
		if (!this.ctx) return;
		
		const particleCountMap: Partial<Record<EvolutionStage, number>> = {
			transcendent: 20,
			mystical: 35,
			divine: 50,
			cosmic: 70,
			infinite: 100
		};
		
		const particleCount = particleCountMap[stage] || 20;
		
		const time = Date.now() / 1000;
		
		for (let i = 0; i < particleCount; i++) {
			// Deterministic particle positions based on index and time
			const angle = (i / particleCount) * Math.PI * 2 + time * 0.1;
			const radius = (Math.sin(time * 0.5 + i) + 1) * 0.5;
			const x = this.canvas.width / 2 + Math.cos(angle) * radius * this.canvas.width * 0.4;
			const y = this.canvas.height / 2 + Math.sin(angle) * radius * this.canvas.height * 0.4;
			
			const size = Math.max(0.1, 1 + Math.sin(time * 2 + i) * 1.5);
			const opacity = 0.2 + Math.sin(time * 3 + i * 0.5) * 0.15;
			
			// Color based on stage
			let color = 'rgba(200, 200, 255, ';
			if (stage === 'mystical') {
				const hue = ((time * 50 + i * 10) % 360);
				color = `hsla(${hue}, 80%, 70%, `;
			} else if (stage === 'divine') {
				color = 'rgba(255, 245, 200, ';
			} else if (stage === 'cosmic') {
				color = 'rgba(150, 150, 255, ';
			} else if (stage === 'infinite') {
				const hue = ((time * 100 + i * 20) % 360);
				color = `hsla(${hue}, 100%, 80%, `;
			}
			
			this.ctx.save();
			this.ctx.globalAlpha = opacity;
			this.ctx.fillStyle = color + opacity + ')';
			this.ctx.beginPath();
			this.ctx.arc(x, y, size, 0, Math.PI * 2);
			this.ctx.fill();
			this.ctx.restore();
		}
	}
	
	// Get visual properties for a node (evolution stage, energy, wisdom, etc.)
	private getNodeVisualProperties(node: Node): VisualProperties {
		// Get evolution stage from network or calculate from node properties
		const stage = this.getNetworkEvolutionStage();
		
		// Normalize node properties to 0-1 range
		const energy = Math.min(1.0, node.energy / 100);
		const value = Math.min(1.0, node.value / 10);
		
		// Get property values from network systems if available
		let wisdom = 0.5;
		let love = 0.5;
		let beauty = 0.5;
		let truth = 0.5;
		let transcendence = 0.5;
		
		// Calculate from network stats
		const nodeCount = this.network.nodes.length;
		const avgEnergy = this.network.nodes.reduce((sum, n) => sum + n.energy, 0) / (nodeCount || 1);
		const nodeLimit = (this.network as any).getCurrentNodeLimit?.() ?? this.network.maxNodesPerCreature ?? 369;
		wisdom = Math.min(1.0, nodeCount / Math.max(1, nodeLimit) * 2);
		transcendence = Math.min(1.0, avgEnergy / 50);
		beauty = Math.min(1.0, (this.network.edges.length / (nodeCount || 1)) / 5);
		truth = 0.5 + Math.random() * 0.3;
		love = 0.5 + Math.random() * 0.3;
		
		return {
			stage,
			energy,
			wisdom,
			love,
			beauty,
			truth,
			transcendence,
			nodeType: node.nodeType || node.linguisticType
		};
	}
	
	// Determine overall network evolution stage
	private getNetworkEvolutionStage(): EvolutionStage {
		// Calculate from network properties
		const nodeCount = this.network.nodes.length;
		const avgEnergy = this.network.nodes.reduce((sum, n) => sum + n.energy, 0) / (nodeCount || 1);
		const nodeLimit = (this.network as any).getCurrentNodeLimit?.() ?? this.network.maxNodesPerCreature ?? 369;
		const complexity = (nodeCount / Math.max(1, nodeLimit) + Math.min(1, avgEnergy / 50)) / 2;
		
		if (complexity < 0.1) return 'embryonic';
		if (complexity < 0.2) return 'primitive';
		if (complexity < 0.4) return 'specialized';
		if (complexity < 0.6) return 'integrated';
		if (complexity < 0.75) return 'transcendent';
		if (complexity < 0.85) return 'mystical';
		if (complexity < 0.95) return 'divine';
		if (complexity < 0.99) return 'cosmic';
		return 'infinite';
	}

	}
